import {
  Rocket,
  BookOpen,
  Settings,
  AlertCircle,
  Shield,
  CreditCard,
  Zap,
  BarChart3,
  Users,
  Globe,
  FileText,
  Bell,
  Lock,
  Key,
  Mail,
  UserPlus,
  Trash2,
  Link2,
  Download,
  Palette,
  Server,
  Activity,
  HelpCircle,
  Scale,
} from 'lucide-react'

export type HelpArticle = {
  id: string
  slug: string
  title: string
  excerpt: string
  content: string
  category:
    | 'Getting Started'
    | 'Integrations'
    | 'Troubleshooting'
    | 'Best Practices'
    | 'Billing'
  tags: string[]
  author: string
  publishedDate: string
  lastUpdated: string
  readTime: string
  views: number
  icon: any
  faqs?: { question: string; answer: string }[]
  relatedArticles?: string[]
}

export const helpArticles: HelpArticle[] = [
  // GETTING STARTED
  {
    id: '1',
    slug: 'welcome-to-flowsense',
    title: 'Welcome to Flowsense: What it does and how to get value fast',
    excerpt:
      'Flowsense helps you audit Klaviyo flows and generate clear, prioritized recommendations so you can improve performance and deliver client-ready reports faster.',
    content: `
## Quick Answer

Flowsense helps you audit Klaviyo flows and generate clear, prioritized recommendations so you can improve performance and deliver client-ready reports faster—especially if you're an agency managing multiple accounts.

## When to Use This

- You just signed up and want the "what is this / what do I do next" path
- You're trying to explain Flowsense to a teammate or client
- You want to know what Flowsense does without touching your Klaviyo setup yet

## What Flowsense Does (Plain English)

**Analyzes your flows** and highlights issues that commonly reduce revenue, engagement, or deliverability.

**Creates recommendations** that you can implement in Klaviyo.

**Helps agencies standardize audits** across many clients and turn audits into repeatable deliverables.

## What Flowsense Does NOT Do

- It does **not** automatically change your Klaviyo flows
- It does **not** send emails on your behalf
- It does **not** require access to your customer list contents for value (it focuses on flow structure + performance context)

## Your Fastest Path to Value (10–30 minutes)

1. Create an account and verify your email
2. Create a workspace (Agency or Brand)
3. Connect Klaviyo
4. Run an audit for your top lifecycle flows
5. Implement the top 3 "high impact, low effort" recommendations
6. Re-audit and export a report

## Troubleshooting Mindset

If something looks "wrong":

1. First confirm you connected the correct Klaviyo account
2. Then re-run the audit
3. If data still looks off, contact support with an org ID + screenshots

## Escalation (What to Send Support)

- Your workspace/org name
- Klaviyo account name (as shown in Klaviyo)
- What you expected to see vs what you saw
- Screenshot + timestamp + browser
    `,
    category: 'Getting Started',
    tags: ['welcome', 'overview', 'getting started', 'introduction'],
    author: 'Flowsense Team',
    publishedDate: '2024-01-15',
    lastUpdated: '2024-03-20',
    readTime: '5 min read',
    views: 0,
    icon: Rocket,
    faqs: [
      {
        question: 'What exactly does Flowsense audit?',
        answer:
          'Your Klaviyo flows (structure + common best-practice checks) and produces actionable recommendations.',
      },
      {
        question: 'Will Flowsense edit my flows automatically?',
        answer:
          'No. You stay in control. Flowsense provides guidance; you implement changes in Klaviyo.',
      },
      {
        question: 'Is Flowsense better for agencies or brands?',
        answer:
          "Both, but it's especially valuable for agencies needing consistent audits and client-ready reporting.",
      },
      {
        question: 'How long does an audit take?',
        answer:
          'Usually minutes. Larger accounts or first-time syncs can take longer.',
      },
      {
        question: 'What do I need before I start?',
        answer:
          'Access to the Klaviyo account you want to audit + a verified email in Flowsense.',
      },
    ],
    relatedArticles: ['2', '3', '15'],
  },
  {
    id: '2',
    slug: 'first-time-setup-checklist',
    title: 'First-time Setup Checklist (10 minutes)',
    excerpt:
      'Follow this checklist to go from "new account" to "first audit" quickly.',
    content: `
## Quick Answer

Follow this checklist to go from "new account" to "first audit" quickly.

## Step-by-Step

### 1. Create Your Flowsense Account
Use an email you can receive mail on (verification + MFA codes).

### 2. Verify Your Email
Open the verification email and click the link.

### 3. Create Your Workspace
- Choose **Agency** if you manage multiple clients
- Choose **Brand** if you're a single brand team

### 4. Connect Klaviyo
- Choose OAuth when possible
- Use API key only if needed (or client policy requires it)

### 5. Run Your First Audit
Start with your most revenue-critical lifecycle flows.

### 6. Review Top Recommendations
Prioritize high impact + lowest effort first.

### 7. Export a Report
Share internally or with a client.

## Troubleshooting

- If verification link doesn't work, resend it from the login page
- If Klaviyo connection shows "no data," confirm permissions and account selection

## Escalation

Send support:
- org/workspace name
- what step you're stuck on
- screenshot of the error
    `,
    category: 'Getting Started',
    tags: ['setup', 'onboarding', 'checklist', 'quickstart'],
    author: 'Flowsense Team',
    publishedDate: '2024-01-15',
    lastUpdated: '2024-03-20',
    readTime: '3 min read',
    views: 0,
    icon: Rocket,
    faqs: [
      {
        question: 'Do I need to verify my email?',
        answer:
          'Yes—verification protects accounts and ensures you can receive security/billing emails.',
      },
      {
        question: 'Should an agency use one workspace or many?',
        answer:
          'Usually one agency workspace with clients tracked inside; use multiple only if you need strict separation.',
      },
      {
        question: 'Can I change workspace type later?',
        answer:
          "In many systems it's not trivial; contact support if you need to convert.",
      },
      {
        question: "What if my client won't allow OAuth?",
        answer: 'Use the API key method (if supported), and keep keys secured.',
      },
      {
        question: 'What is the "first audit" supposed to produce?',
        answer: 'A list of prioritized recommendations + a structured report.',
      },
    ],
    relatedArticles: ['1', '15', '9'],
  },
  {
    id: '3',
    slug: 'understanding-dashboard',
    title: 'Understanding Your Dashboard (Agency-focused)',
    excerpt:
      'Navigate your Flowsense dashboard efficiently and understand what each section shows you.',
    content: `
## Quick Answer

Your dashboard is organized to give you quick access to audits, recommendations, reports, and settings. For agencies, it's designed to switch between clients seamlessly.

## Dashboard Sections

### Overview
- Quick health scores across all flows
- Recent activity and alerts
- Key metrics at a glance

### Flows
- List of all connected Klaviyo flows
- Health scores and status indicators
- Quick access to run audits

### Recommendations
- Prioritized list of improvements
- Filter by severity, effort, and category
- Track implementation status

### Reports
- Export history
- Scheduled reports (Agency plan)
- White-label settings

### Settings
- Integrations (Klaviyo connections)
- Team management
- Billing and subscription
- Branding and custom domains

## Agency-Specific Features

**Client Switcher**: Quickly switch between client accounts without logging out.

**Bulk Operations**: Run audits across multiple clients at once.

**White-Label Reports**: Export client-ready reports with your branding.

## Best Practices

- Bookmark frequently accessed client dashboards
- Use filters to focus on high-priority recommendations
- Set up scheduled reports for regular client check-ins
- Keep your team roles organized for proper access control
    `,
    category: 'Getting Started',
    tags: ['dashboard', 'navigation', 'interface', 'agency'],
    author: 'Flowsense Product',
    publishedDate: '2024-02-01',
    lastUpdated: '2024-03-15',
    readTime: '4 min read',
    views: 0,
    icon: BarChart3,
    relatedArticles: ['1', '9', '17'],
  },
  {
    id: '4',
    slug: 'brand-vs-agency-workspaces',
    title: 'Brand vs Agency Workspaces: Choosing the Right Setup',
    excerpt:
      'Understand the difference between Brand and Agency workspaces and choose the right structure for your needs.',
    content: `
## Quick Answer

**Brand workspaces** are for single companies managing their own Klaviyo account. **Agency workspaces** are for agencies managing multiple client accounts with white-label capabilities.

## When to Choose Brand

- You're a single company/brand
- You manage one Klaviyo account
- You don't need white-label reports
- You want simpler billing

## When to Choose Agency

- You manage multiple client accounts
- You need white-label reports and branding
- You want client-specific access controls
- You need team collaboration features

## Key Differences

### Brand Workspace
- Single Klaviyo connection
- Standard reporting
- Team collaboration (limited)
- Simpler pricing

### Agency Workspace
- Multiple client accounts
- White-label reports
- Custom domains
- Per-client pricing
- Advanced team roles
- Scheduled reports

## Migration Path

If you start with Brand and need to upgrade to Agency:
1. Contact support with your workspace name
2. We'll help migrate your data
3. You'll gain access to agency features
4. Billing will adjust to agency pricing

## Best Practices

**For Agencies:**
- Use consistent naming conventions for clients
- Set up white-label branding early
- Assign team roles based on client access needs
- Use scheduled reports for regular client updates

**For Brands:**
- Keep your workspace focused on your single account
- Upgrade to Agency only if you start managing other brands
    `,
    category: 'Getting Started',
    tags: ['workspace', 'agency', 'brand', 'setup', 'organization'],
    author: 'Flowsense Team',
    publishedDate: '2024-02-05',
    lastUpdated: '2024-03-10',
    readTime: '4 min read',
    views: 0,
    icon: Users,
    faqs: [
      {
        question: 'Can I switch from Brand to Agency later?',
        answer:
          "Yes, contact support and we'll help you migrate. Your data will be preserved.",
      },
      {
        question: 'Do I pay more for Agency?',
        answer:
          'Agency has different pricing: base fee + per-client fees. See our pricing page for details.',
      },
      {
        question: 'Can a Brand workspace have multiple team members?',
        answer:
          'Yes, but with limited collaboration features compared to Agency.',
      },
    ],
    relatedArticles: ['9', '10', '11'],
  },

  // AUTHENTICATION & SECURITY
  {
    id: '5',
    slug: 'login-issues-troubleshooting',
    title: "Login Issues: Can't Sign In / Locked Out",
    excerpt:
      'Troubleshoot common login problems including locked accounts, forgotten passwords, and authentication errors.',
    content: `
## Quick Answer

If you can't log in, it's usually one of: wrong email/password, unverified email, expired session, or account locked due to security.

## Common Login Issues

### Wrong Email or Password
1. Confirm you're using the correct email
2. Try password reset if unsure
3. Check for typos (especially in password)

### Email Not Verified
1. Check your inbox for verification email
2. Click "Resend verification" on login page
3. Check spam/promotions folder

### Account Locked
Usually happens after multiple failed login attempts.
- Wait 30 minutes and try again
- Or contact support to unlock immediately

### Session Expired
- Clear browser cookies
- Try incognito/private window
- Log out completely and log back in

## Step-by-Step: Reset Your Access

1. Go to login page
2. Click "Forgot password?"
3. Enter your email
4. Check inbox for reset link
5. Create new password
6. Log in with new credentials

## Troubleshooting Checklist

- [ ] Correct email address
- [ ] Email is verified
- [ ] Password is correct (try reset if unsure)
- [ ] Browser cookies enabled
- [ ] Not using VPN that might be blocked
- [ ] Account not locked (wait 30 min)

## Escalation

If none of the above works, contact support with:
- Email address you're trying to use
- Screenshot of error message
- Timestamp of last successful login
- Browser and device info
    `,
    category: 'Getting Started',
    tags: [
      'login',
      'authentication',
      'password',
      'locked account',
      'troubleshooting',
    ],
    author: 'Flowsense Support',
    publishedDate: '2024-01-20',
    lastUpdated: '2024-03-18',
    readTime: '3 min read',
    views: 0,
    icon: Lock,
    faqs: [
      {
        question: 'Why is my account locked?',
        answer:
          'Accounts lock after multiple failed login attempts for security. Wait 30 minutes or contact support.',
      },
      {
        question: 'I never received a verification email',
        answer:
          'Check spam/promotions, then use "Resend verification" on the login page.',
      },
      {
        question: 'Can I use SSO or social login?',
        answer:
          'Currently we support email/password only. SSO may be available for Enterprise plans.',
      },
    ],
    relatedArticles: ['6', '7', '8'],
  },
  {
    id: '6',
    slug: 'email-verification',
    title: 'Email Verification: How It Works + Resend Verification',
    excerpt:
      'Understand email verification requirements and how to resend verification links if needed.',
    content: `
## Quick Answer

Email verification protects your account and ensures you can receive important security and billing notifications.

## How Verification Works

1. You sign up with an email
2. We send a verification link
3. Click the link to verify
4. Your account is activated

## If You Didn't Receive the Email

### Check These First
- Spam/Junk folder
- Promotions tab (Gmail)
- Correct email address was used

### Resend Verification
1. Go to login page
2. Enter your email
3. Click "Resend verification email"
4. Check inbox again (including spam)

## Verification Link Expired

Links expire after 24 hours for security.
- Request a new link using "Resend verification"
- New link will be valid for another 24 hours

## Troubleshooting

**Email never arrives:**
- Add support@useflowsense.com to contacts
- Check email provider isn't blocking us
- Try a different email address

**Link doesn't work:**
- Make sure you're clicking the most recent link
- Try copying/pasting the full URL
- Clear browser cache and try again

## Escalation

Contact support with:
- Email address you signed up with
- Approximate signup time
- Screenshot of any error messages
    `,
    category: 'Getting Started',
    tags: ['verification', 'email', 'activation', 'signup'],
    author: 'Flowsense Support',
    publishedDate: '2024-01-20',
    lastUpdated: '2024-03-18',
    readTime: '2 min read',
    views: 0,
    icon: Mail,
    faqs: [
      {
        question: 'How long is the verification link valid?',
        answer: '24 hours. After that, request a new one.',
      },
      {
        question: 'Can I change my email after signing up?',
        answer:
          "Yes, in Settings → Profile. You'll need to verify the new email.",
      },
      {
        question: 'Do I need to verify every time I log in?',
        answer: 'No, only once when you first create your account.',
      },
    ],
    relatedArticles: ['5', '7', '2'],
  },
  {
    id: '7',
    slug: 'password-reset',
    title: 'Password Reset (Forgot Password)',
    excerpt:
      "Step-by-step guide to resetting your password if you've forgotten it or need to change it.",
    content: `
## Quick Answer

Use the "Forgot password?" link on the login page to receive a password reset email.

## Step-by-Step: Reset Your Password

1. Go to the login page
2. Click "Forgot password?"
3. Enter your email address
4. Check your inbox for reset email
5. Click the reset link
6. Create a new password
7. Log in with new password

## Password Requirements

Your new password must:
- Be at least 8 characters long
- Include at least one uppercase letter
- Include at least one number
- Not be a commonly used password

## If You Don't Receive the Email

1. Check spam/junk folder
2. Verify you entered the correct email
3. Wait a few minutes (delivery can be delayed)
4. Try requesting reset again
5. Add support@useflowsense.com to contacts

## Reset Link Expired

Password reset links expire after 1 hour for security.
- Request a new reset link
- Complete the process within 1 hour

## Changing Your Password (When Logged In)

If you're already logged in and want to change your password:
1. Go to Settings → Security
2. Click "Change password"
3. Enter current password
4. Enter new password
5. Confirm new password
6. Save changes

## Troubleshooting

**Reset link doesn't work:**
- Ensure you're using the most recent link
- Try copying the full URL into your browser
- Request a new link if it's been over an hour

**"Password too weak" error:**
- Add more characters
- Include uppercase, numbers, and symbols
- Avoid common words or patterns

## Escalation

Contact support with:
- Email address
- Timestamp of reset request
- Screenshot of any error messages
    `,
    category: 'Getting Started',
    tags: ['password', 'reset', 'security', 'login'],
    author: 'Flowsense Support',
    publishedDate: '2024-01-20',
    lastUpdated: '2024-03-18',
    readTime: '3 min read',
    views: 0,
    icon: Key,
    faqs: [
      {
        question: 'How long is the reset link valid?',
        answer: '1 hour for security. Request a new one if it expires.',
      },
      {
        question: 'Will resetting my password log me out everywhere?',
        answer: 'Yes, for security all active sessions will be terminated.',
      },
      {
        question: 'Can I reuse an old password?',
        answer: 'We recommend using a new, unique password for security.',
      },
    ],
    relatedArticles: ['5', '6', '8'],
  },
  {
    id: '8',
    slug: 'mfa-setup-troubleshooting',
    title: 'MFA (Email Codes): Setup and Troubleshooting',
    excerpt:
      'Set up multi-factor authentication using email codes for enhanced account security.',
    content: `
## Quick Answer

MFA adds an extra security layer by requiring a code sent to your email in addition to your password.

## How MFA Works

1. You log in with email + password
2. We send a 6-digit code to your email
3. You enter the code to complete login
4. Code expires after 10 minutes

## Enabling MFA

1. Go to Settings → Security
2. Toggle "Enable MFA"
3. Confirm your email
4. Test it by logging out and back in

## Using MFA Codes

**When you log in:**
1. Enter email + password
2. Check your email for the code
3. Enter the 6-digit code
4. Click "Verify"

**Code not received:**
- Check spam/junk folder
- Wait 1-2 minutes (delivery can be delayed)
- Click "Resend code"

## Troubleshooting

**Code expired:**
- Codes expire after 10 minutes
- Request a new code

**Code doesn't work:**
- Ensure you're using the most recent code
- Check for typos
- Request a new code

**Can't access email:**
- Contact support immediately
- Provide proof of account ownership
- We'll help you regain access

## Disabling MFA

If you need to disable MFA:
1. Log in (using MFA)
2. Go to Settings → Security
3. Toggle off "Enable MFA"
4. Confirm the change

**Note:** We recommend keeping MFA enabled for security.

## Best Practices

- Keep your email secure
- Don't share MFA codes
- Use a reliable email provider
- Add support@useflowsense.com to contacts

## Escalation

If locked out, contact support with:
- Account email
- Proof of ownership (recent invoice, etc.)
- Alternative contact method
    `,
    category: 'Getting Started',
    tags: ['mfa', 'security', 'authentication', 'two-factor'],
    author: 'Flowsense Security',
    publishedDate: '2024-02-01',
    lastUpdated: '2024-03-15',
    readTime: '3 min read',
    views: 0,
    icon: Shield,
    faqs: [
      {
        question: 'Is MFA required?',
        answer: 'Not required but strongly recommended for account security.',
      },
      {
        question: 'What if I lose access to my email?',
        answer: 'Contact support immediately with proof of account ownership.',
      },
      {
        question: 'Can I use an authenticator app instead?',
        answer:
          'Currently we support email codes only. Authenticator app support may come in the future.',
      },
    ],
    relatedArticles: ['5', '7', '26'],
  },

  // BILLING
  {
    id: '9',
    slug: 'billing-faq',
    title: 'Billing FAQ (Quick Answers)',
    excerpt:
      'Quick answers to common billing questions about pricing, invoices, cancellations, and refunds.',
    content: `
## Quick Answer

Billing is managed via Stripe. You can upgrade/downgrade, manage invoices, and request refunds under the policy below.

## Key Points

- Billing is subscription-based (monthly or annual depending on plan)
- Agencies may have unit-based pricing (e.g., per client beyond base)
- Invoices and payment method updates happen in the customer portal
- Refunds: eligible within 14 days of the last payment only

## Where to Manage Billing

In Flowsense: **Billing page** → "Manage Subscription" → Stripe portal

## Common Questions

### Where do I download invoices?
In the Stripe customer portal linked from your Billing page.

### Can I cancel anytime?
Yes—cancellation usually applies at end of the current billing period unless otherwise stated.

### Do you offer refunds?
Yes: within 14 days of the last payment only (see refund article).

### How do I change my card?
Stripe portal → update payment method.

### Why was my invoice higher this month?
Often due to unit/client count changes for agencies, or plan changes.

## Escalation (Billing)

Send:
- Organization/workspace name
- Invoice ID (preferred)
- The email used for billing
- What you want to happen (refund, cancellation, invoice edit)

## Related Topics

- Plans & Pricing Explained
- Upgrading to Pro
- Managing Subscription
- Refund Policy
- Failed Payments
    `,
    category: 'Billing',
    tags: ['billing', 'faq', 'invoices', 'subscription', 'payment'],
    author: 'Flowsense Billing',
    publishedDate: '2024-01-15',
    lastUpdated: '2024-03-20',
    readTime: '2 min read',
    views: 0,
    icon: CreditCard,
    relatedArticles: ['10', '11', '13'],
  },
  {
    id: '10',
    slug: 'plans-pricing-explained',
    title: 'Plans & Pricing Explained (Agency vs Brand)',
    excerpt:
      "Detailed breakdown of Free, Pro, and Agency plans and what's included in each tier.",
    content: `
## Quick Answer

Flowsense offers three main tiers designed for different stages of growth: Free, Pro, and Agency.

## Free Tier

Perfect for testing the waters.

**Included:**
- 1 Connected Account
- Basic Health Scores
- 7-Day Data History
- Live Chatbot Support

**Best for:** Testing Flowsense before committing

## Pro Tier ($250/year or $30/month)

For growing brands serious about optimization.

**Included:**
- Unlimited Flow Analysis
- Advanced AI Recommendations
- Full Data History
- Live Chatbot Support
- Flow Error Alerting
- Export Reports

**Best for:** Single brands managing their own Klaviyo

## Agency Tier ($250/year + client fees)

Built for marketing agencies managing multiple accounts.

**Base Price:** $250/year or $30/month
**Per Client:** $150/year or $20/month (first client included)

**Included:**
- Everything in Pro
- White-Label Dashboard & Reports
- Multi-Client Management
- Team Collaboration Tools
- Custom Domains
- Scheduled Reports
- Live Chatbot Support

**Best for:** Agencies managing 2+ client accounts

## Pricing Examples

### Agency with 5 Clients
- Base: $250/year
- Additional 4 clients: 4 × $150 = $600/year
- **Total: $850/year**

### Agency with 10 Clients
- Base: $250/year
- Additional 9 clients: 9 × $150 = $1,350/year
- **Total: $1,600/year**

## Choosing the Right Plan

**Choose Free if:**
- You're just testing Flowsense
- You manage 1 flow and don't need history

**Choose Pro if:**
- You're a single brand
- You need full features for one account
- You don't need white-label

**Choose Agency if:**
- You manage multiple client accounts
- You need white-label reports
- You want team collaboration

## Upgrading

You can upgrade at any time. See "Upgrading to Pro" article for step-by-step instructions.
    `,
    category: 'Billing',
    tags: ['pricing', 'plans', 'features', 'comparison', 'agency'],
    author: 'Flowsense Sales',
    publishedDate: '2024-01-10',
    lastUpdated: '2024-03-20',
    readTime: '4 min read',
    views: 0,
    icon: CreditCard,
    faqs: [
      {
        question: 'Can I switch from Brand to Agency later?',
        answer:
          'Yes, upgrade through the billing page or contact support for help.',
      },
      {
        question: 'Do I pay more for Agency?',
        answer:
          'Agency has base fee + per-client fees. First client is included in base price.',
      },
      {
        question: 'What happens if I exceed my plan limits?',
        answer:
          "You'll see a prompt to upgrade. Your account won't be disrupted.",
      },
    ],
    relatedArticles: ['9', '11', '4'],
  },
  {
    id: '11',
    slug: 'upgrading-to-pro',
    title: 'Upgrading to Pro (Stripe Checkout): Step-by-Step',
    excerpt:
      'Instructions for upgrading your subscription to Pro or Agency plan via Stripe checkout.',
    content: `
## Quick Answer

Upgrade through the Billing page to unlock Pro or Agency features. Payment is processed securely via Stripe.

## Step-by-Step: Upgrade to Pro

1. Log in to Flowsense
2. Go to **Billing** page
3. Click **Upgrade to Pro** button
4. You'll be redirected to Stripe checkout
5. Enter payment details:
   - Card number
   - Expiration date
   - CVC
   - Billing address
6. Review plan details and price
7. Click **Subscribe**
8. You'll be redirected back to Flowsense
9. Your plan will update within minutes

## Step-by-Step: Upgrade to Agency

1. Go to **Billing** page
2. Click **Upgrade to Agency**
3. Select number of clients
4. Review total price
5. Complete Stripe checkout
6. Return to Flowsense
7. Your agency features will be activated

## What Happens After Upgrade

- Features unlock immediately
- You'll receive a confirmation email
- Invoice is available in Stripe portal
- Billing cycle starts from upgrade date

## Prorated Billing

If you upgrade mid-cycle:
- You'll be charged a prorated amount
- Remaining time on old plan is credited
- New features available immediately

## Troubleshooting

**Payment declined:**
- Verify card details are correct
- Check with your bank
- Try a different card
- Contact support if issue persists

**Plan didn't update:**
- Wait 2-3 minutes for sync
- Refresh the page
- Log out and back in
- Contact support with invoice ID

**Wrong plan selected:**
- Contact support immediately
- We can switch plans or refund (within 14 days)

## Escalation

Contact support with:
- Workspace/org name
- Invoice ID from Stripe
- Screenshot of billing page
- What plan you intended to purchase
    `,
    category: 'Billing',
    tags: ['upgrade', 'subscription', 'payment', 'stripe', 'checkout'],
    author: 'Flowsense Billing',
    publishedDate: '2024-02-05',
    lastUpdated: '2024-03-15',
    readTime: '3 min read',
    views: 0,
    icon: CreditCard,
    faqs: [
      {
        question: 'Will I lose data if I upgrade?',
        answer: 'No, all your data and history are preserved when you upgrade.',
      },
      {
        question: 'Can I cancel after upgrading?',
        answer:
          'Yes, you can cancel anytime. Access continues until end of billing period.',
      },
      {
        question: 'Do you store my credit card?',
        answer:
          'No, Stripe securely stores payment details. We never see your full card number.',
      },
    ],
    relatedArticles: ['9', '10', '12'],
  },
  {
    id: '12',
    slug: 'managing-subscription',
    title:
      'Managing Subscription (Portal): Invoices, Cancellation, Payment Method',
    excerpt:
      'How to manage your subscription, download invoices, update payment methods, and cancel if needed.',
    content: `
## Quick Answer

Manage your subscription through the Stripe customer portal accessible from your Billing page.

## Accessing the Customer Portal

1. Go to **Billing** page in Flowsense
2. Click **Manage Subscription**
3. You'll be redirected to Stripe portal
4. Make changes as needed

## What You Can Do in the Portal

### Download Invoices
- View all past invoices
- Download as PDF
- Print for accounting

### Update Payment Method
- Add new card
- Remove old cards
- Set default payment method
- Update billing address

### View Subscription Details
- Current plan
- Next billing date
- Billing amount
- Billing history

### Cancel Subscription
- Cancel anytime
- Access continues until period end
- No refund for unused time (unless within 14-day window)

## Updating Your Payment Method

1. Open customer portal
2. Click **Update payment method**
3. Enter new card details
4. Save changes
5. New card will be used for next billing

## Canceling Your Subscription

### Before You Cancel

- Export any reports you need
- Download invoices for records
- Note: cancellation takes effect at period end

### Steps to Cancel

1. Open customer portal
2. Click **Cancel subscription**
3. Select reason (optional)
4. Confirm cancellation
5. You'll receive confirmation email

### What Happens After Cancellation

- Access continues until period end
- No future charges
- Account downgrades to Free plan
- Data is retained (not deleted)

## Reactivating a Canceled Subscription

1. Go to Billing page
2. Click **Reactivate** or **Upgrade**
3. Complete checkout
4. Subscription resumes immediately

## Troubleshooting

**Can't access portal:**
- Ensure you're logged in
- Try clearing browser cache
- Contact support if link doesn't work

**Payment update failed:**
- Verify card details
- Check with your bank
- Try a different card

**Cancellation didn't process:**
- Check confirmation email
- Verify in customer portal
- Contact support with screenshot

## Escalation

Contact support with:
- Workspace/org name
- What you're trying to do
- Screenshot of any errors
- Invoice ID (if relevant)
    `,
    category: 'Billing',
    tags: ['subscription', 'management', 'invoices', 'cancellation', 'stripe'],
    author: 'Flowsense Billing',
    publishedDate: '2024-02-05',
    lastUpdated: '2024-03-15',
    readTime: '4 min read',
    views: 0,
    icon: CreditCard,
    faqs: [
      {
        question: 'Where do I find my invoices?',
        answer:
          'In the Stripe customer portal, accessible from your Billing page.',
      },
      {
        question: 'Can I cancel anytime?',
        answer:
          'Yes, cancellation takes effect at the end of your current billing period.',
      },
      {
        question: 'Will canceling delete my data?',
        answer: "No, your data is retained. You'll downgrade to Free plan.",
      },
      {
        question: 'How do I change my billing email?',
        answer: 'Update it in the Stripe portal under billing details.',
      },
    ],
    relatedArticles: ['9', '11', '13'],
  },
  {
    id: '13',
    slug: 'refund-policy',
    title: 'Refund Policy (14 Days, Last Payment Only) + How to Request',
    excerpt:
      'Understand our 14-day refund policy and learn how to request a refund if eligible.',
    content: `
## Quick Answer

Refunds are available within 14 days of your last payment only. If eligible, we'll refund the most recent charge (not older charges).

## What "Eligible" Means (Plain Language)

- You're within 14 days of the charge date for the most recent payment
- The request is for the last payment only
- You provide enough information to verify the charge and workspace

## How to Request a Refund (Step-by-Step)

1. Go to your **Billing** page and open the Stripe portal
2. Find the most recent invoice/payment date
3. Contact support with:
   - Workspace/org name
   - Invoice ID (best)
   - Reason for refund request
4. Support confirms eligibility and processes the refund
5. You'll receive confirmation (timing depends on bank)

## What Refunds Do NOT Cover

- Payments older than the most recent charge
- Charges outside the 14-day window
- Custom services (if applicable)
- Partial month refunds after 14 days

## Refund Processing Time

- Approval: Usually within 1 business day
- Bank processing: 5-10 business days
- You'll receive email confirmation

## Troubleshooting

**If you canceled but still got charged:**
- Check cancellation date vs renewal date
- Cancellation takes effect at period end
- If charged after cancellation, contact support

**If you have multiple workspaces:**
- Confirm which workspace the invoice belongs to
- Provide correct workspace name and invoice ID

## Escalation (Fastest Processing)

Send to support:
- Invoice ID
- Org/workspace name
- Billing email
- Last 4 digits (optional) and charge date
- "I'm requesting a refund under the 14-day last payment policy."

## After Refund

- Your plan will downgrade to Free
- Access to paid features ends immediately
- Data is retained (not deleted)
- You can upgrade again anytime
    `,
    category: 'Billing',
    tags: ['refund', 'policy', 'money-back', 'cancellation'],
    author: 'Flowsense Billing',
    publishedDate: '2024-01-15',
    lastUpdated: '2024-03-20',
    readTime: '3 min read',
    views: 0,
    icon: CreditCard,
    faqs: [
      {
        question:
          'Can I get a refund for payments older than the last payment?',
        answer: 'No—refunds apply to the last payment only, within 14 days.',
      },
      {
        question: 'I forgot to cancel—can I still get a refund?',
        answer:
          "Possibly, if it's within 14 days of the last payment. Contact support with your invoice ID.",
      },
      {
        question: 'How long do refunds take?',
        answer: 'Usually a few business days, depending on your bank.',
      },
      {
        question: 'Will canceling automatically refund me?',
        answer:
          'No—cancellation stops future renewals; refunds require a request.',
      },
      {
        question: 'What if I filed a chargeback already?',
        answer:
          'Contact support immediately—chargebacks slow resolution and can affect account standing.',
      },
    ],
    relatedArticles: ['9', '12', '14'],
  },
  {
    id: '14',
    slug: 'failed-payments-past-due',
    title: 'Failed Payments / Past Due: How to Fix',
    excerpt:
      'Resolve failed payment issues and restore your account access when payments are past due.',
    content: `
## Quick Answer

If your payment fails, we'll retry automatically. If all retries fail, your account may be suspended or downgraded.

## What Happens When Payment Fails

1. **First attempt fails** → We retry in 3 days
2. **Second retry fails** → We retry in 5 days
3. **Third retry fails** → Account suspended or downgraded

You'll receive email notifications at each step.

## How to Fix Failed Payment

### Update Your Payment Method

1. Go to **Billing** page
2. Click **Manage Subscription**
3. Update payment method in Stripe portal
4. Save new card
5. We'll retry charging automatically

### Manual Retry

If you've updated your card:
1. Contact support
2. We can manually retry the charge
3. Or you can wait for automatic retry

## Common Causes of Failed Payments

**Insufficient funds:**
- Ensure card has available balance
- Check with your bank

**Expired card:**
- Update to a card with future expiration
- Remove old cards from portal

**Bank declined:**
- Contact your bank
- They may have flagged the charge
- Authorize international transactions if needed

**Incorrect billing address:**
- Update address in Stripe portal
- Ensure it matches bank records

## What Happens to Your Account

**During grace period:**
- Full access continues
- You'll receive reminder emails

**After final retry fails:**
- Account may be suspended
- Or downgraded to Free plan
- Paid features become unavailable

## Restoring Access

1. Update payment method
2. Contact support to retry charge
3. Or upgrade again through checkout
4. Access restores immediately upon successful payment

## Preventing Future Issues

- Keep payment method current
- Update before card expires
- Ensure sufficient funds
- Whitelist Flowsense charges with bank
- Monitor billing emails

## Escalation

Contact support with:
- Workspace/org name
- Invoice ID of failed payment
- Confirmation that payment method is updated
- Request manual retry
    `,
    category: 'Billing',
    tags: ['payment', 'failed', 'past due', 'suspended', 'troubleshooting'],
    author: 'Flowsense Billing',
    publishedDate: '2024-02-10',
    lastUpdated: '2024-03-15',
    readTime: '3 min read',
    views: 0,
    icon: AlertCircle,
    faqs: [
      {
        question: 'How many times will you retry?',
        answer: 'We retry up to 3 times over approximately 10 days.',
      },
      {
        question: 'Will I lose my data if payment fails?',
        answer:
          "No, data is retained. You'll lose access to paid features until payment succeeds.",
      },
      {
        question: 'Can I update my card before the retry?',
        answer:
          "Yes, update anytime in the Stripe portal. We'll use the new card for retry.",
      },
      {
        question: "What if I don't want to continue?",
        answer:
          'Cancel your subscription in the Stripe portal to stop retry attempts.',
      },
    ],
    relatedArticles: ['9', '12', '15'],
  },
  {
    id: '15',
    slug: 'plan-not-updating-after-payment',
    title: 'Plan Not Updating After Payment (Webhook Sync Expectations)',
    excerpt:
      "Troubleshoot issues when your plan doesn't update immediately after successful payment.",
    content: `
## Quick Answer

Plan updates usually happen within 1-2 minutes after payment. If it takes longer, it's likely a webhook sync delay.

## Normal Sync Timeline

- **Immediate:** Payment processes in Stripe
- **1-2 minutes:** Webhook notifies Flowsense
- **2-3 minutes:** Your plan updates in Flowsense

## What to Do If Plan Doesn't Update

### Wait and Refresh (First Step)

1. Wait 2-3 minutes
2. Refresh the page
3. Log out and back in
4. Check Billing page

### Verify Payment Succeeded

1. Open Stripe customer portal
2. Check if invoice shows "Paid"
3. Verify charge on your card

### If Still Not Updated After 5 Minutes

Contact support with:
- Workspace/org name
- Invoice ID from Stripe
- Screenshot of Billing page
- Timestamp of payment

## Why Delays Happen

**Webhook delays:**
- Network issues between Stripe and Flowsense
- High traffic periods
- Temporary service disruptions

**Cache issues:**
- Browser cache showing old data
- Session needs refresh

## How We Fix It

Support can:
- Manually sync your subscription
- Verify webhook delivery
- Update your plan immediately
- Investigate any webhook failures

## Preventing Issues

- Ensure stable internet during checkout
- Don't close browser immediately after payment
- Wait for confirmation page
- Check email for payment confirmation

## What Features Unlock

After plan updates:
- All Pro/Agency features immediately available
- Team management (if applicable)
- White-label settings (Agency)
- Export capabilities
- Priority support

## Escalation (Fast Track)

For immediate resolution, send:
- Invoice ID (most important)
- Workspace name
- Screenshot showing plan hasn't updated
- Confirmation that payment succeeded in Stripe
    `,
    category: 'Billing',
    tags: ['payment', 'sync', 'webhook', 'upgrade', 'troubleshooting'],
    author: 'Flowsense Engineering',
    publishedDate: '2024-02-15',
    lastUpdated: '2024-03-18',
    readTime: '3 min read',
    views: 0,
    icon: AlertCircle,
    faqs: [
      {
        question: 'How long should I wait before contacting support?',
        answer:
          'Wait 5 minutes, refresh, and log out/in. If still not updated, contact support.',
      },
      {
        question: 'Will I be charged twice if I retry?',
        answer:
          'No, Stripe prevents duplicate charges. Check your invoice history to confirm.',
      },
      {
        question: 'Can I use paid features while waiting?',
        answer: 'No, features unlock only after plan sync completes.',
      },
      {
        question: 'What if I paid but want to cancel?',
        answer:
          'Contact support immediately. Refunds available within 14 days of payment.',
      },
    ],
    relatedArticles: ['11', '14', '9'],
  },

  // ORGANIZATIONS & TEAM
  {
    id: '16',
    slug: 'workspaces-organizations',
    title:
      'Workspaces (Organizations): What They Are and How to Structure Them',
    excerpt:
      'Understand workspaces and learn the best structure for agencies managing multiple clients.',
    content: `
## Quick Answer

A workspace (organization) is the container for your data, settings, billing, integrations, and team access. Agencies typically run one agency workspace and manage multiple clients inside it.

## When to Use This

- You're deciding whether to create one workspace or many
- You're onboarding a new client
- You're trying to understand why teammates can't see the same data

## What's Inside a Workspace

- Your subscription/plan
- Your team members + roles
- Your integrations (e.g., Klaviyo connection(s))
- Your branding / white-label settings
- Your audit and reporting context

## Recommended Setup for Agencies

**Default:** One "Agency" workspace (e.g., "Acme Agency")
- Add client accounts inside as separate client entities
- Use consistent naming conventions

**Use multiple workspaces only if:**
- You need strict legal separation (separate invoices, access controls)
- You run multiple agency brands/teams with distinct billing

## Naming Conventions (Makes Reporting Smoother)

Use: **ClientName — BrandName — Region** (if needed)

Examples:
- "Nike — Sportswear — US"
- "Starbucks — Coffee — Global"
- "LocalBakery — Main"

Avoid:
- "test" or "new" in production client names
- Keep a "Sandbox client" explicitly labeled

## Troubleshooting

**If a teammate sees a different workspace:**
- They might be logged into a different org
- Or lack membership in your workspace

**If data looks "missing":**
- Confirm you're in the right org
- Confirm you're in the right client context

## Escalation (What to Send Support)

- Workspace/org name
- The user email having the issue
- Screenshot of the organization selector + what's missing
    `,
    category: 'Getting Started',
    tags: ['workspace', 'organization', 'structure', 'agency', 'setup'],
    author: 'Flowsense Team',
    publishedDate: '2024-02-01',
    lastUpdated: '2024-03-15',
    readTime: '4 min read',
    views: 0,
    icon: Users,
    faqs: [
      {
        question:
          "What's the difference between my user account and a workspace?",
        answer:
          'Your user is you; the workspace is the shared container your team works inside.',
      },
      {
        question: 'Can one user belong to multiple workspaces?',
        answer: 'Yes (common for agencies and contractors).',
      },
      {
        question: 'Should I make one workspace per client?',
        answer:
          'Usually no. Agencies typically use one workspace unless there are billing/legal reasons to separate.',
      },
      {
        question: 'Can I rename a workspace?',
        answer: 'Yes (if your role allows it).',
      },
      {
        question: 'What happens if I delete a workspace?',
        answer:
          'It removes that workspace and related data/settings; billing impacts depend on subscription state.',
      },
    ],
    relatedArticles: ['17', '18', '4'],
  },
  {
    id: '17',
    slug: 'roles-permissions',
    title: 'Roles & Permissions (Owner/Admin/User/Analyst/Read-only)',
    excerpt:
      'Understand different user roles and what permissions each role has in your workspace.',
    content: `
## Quick Answer

Roles control what someone can view or change. Agencies usually keep Owners/Admins limited and grant User/Analyst/Read-only to client-facing teammates.

## When to Use This

- Someone says "I can't see the Team tab" or "Billing is missing"
- You're setting up safe access for a contractor
- You're defining internal operating procedures for client accounts

## Suggested Role Meanings (Plain English)

### Owner
- Full control, including billing and sensitive settings
- Can delete workspace
- Can transfer ownership

### Admin
- Can manage most settings, team, and workflows
- May include billing access
- Cannot delete workspace

### User
- Can run audits, view reports, make non-critical changes
- Limited settings access
- Cannot manage team or billing

### Analyst
- Can view and analyze data
- Limited editing capabilities
- Good for implementation specialists

### Read-only
- View-only access
- Best for clients who want transparency without risk
- Cannot make any changes

## Best Practices for Agencies

- Keep 1–2 Owners max
- Use Admin for your ops lead
- Use Analyst for implementation specialists
- Use Read-only for clients unless they must collaborate

## Troubleshooting

**If the UI hides features:**
- It's usually role-based
- Check your role in Settings → Team

**If someone can't upgrade:**
- They likely need Owner/Admin role

## Escalation Checklist

- Workspace name
- User email
- What they're trying to do
- Screenshot of missing UI element
    `,
    category: 'Getting Started',
    tags: ['roles', 'permissions', 'access', 'team', 'security'],
    author: 'Flowsense Team',
    publishedDate: '2024-02-01',
    lastUpdated: '2024-03-15',
    readTime: '3 min read',
    views: 2100,
    icon: Shield,
    faqs: [
      {
        question: 'Who can manage billing?',
        answer: 'Typically Owner/Admin only.',
      },
      {
        question: "Why can't I see the Team tab?",
        answer: 'Your role may not allow team management.',
      },
      {
        question: "Why can't I edit branding or domains?",
        answer: 'These are usually restricted to paid plans and higher roles.',
      },
      {
        question: 'Can I make someone read-only?',
        answer: 'Yes—recommended for clients and stakeholders.',
      },
      {
        question: 'Can I transfer ownership?',
        answer: 'Usually yes; if not available in UI, support can help.',
      },
    ],
    relatedArticles: ['16', '18', '19'],
  },
  {
    id: '18',
    slug: 'inviting-teammates',
    title: 'Inviting Teammates: How Invites Work + Troubleshooting',
    excerpt:
      'Learn how to invite team members to your workspace and troubleshoot common invite issues.',
    content: `
## Quick Answer

Invites send an email link that adds a user to your workspace with a chosen role.

## When to Use This

- Onboarding a new teammate
- Giving a client read-only access
- Adding a contractor for a short engagement

## Step-by-Step

1. Go to **Settings → Team**
2. Click **Invite user**
3. Enter email + choose role
4. Send invite
5. Invitee accepts and signs in (or creates account)

## Common Agency Workflows

- Invite client as **Read-only**
- Invite contractor as **Analyst** and remove at end of engagement
- Keep billing access restricted to Owner/Admin

## Troubleshooting

### Invite not received

**Check:**
- Spam/promotions folder
- Email spelling is correct
- Try resending invite
- Ask them to allowlist support@useflowsense.com

### Invite link expired

- Resend invite
- Links expire after 7 days for security

### Invite accepted but user can't see workspace

**Verify:**
- They're logged into the correct email
- Membership exists in Settings → Team
- They're not in a different workspace

## Escalation

Send to support:
- Workspace/org name
- Invited email
- Approx time invite was sent
- Screenshot of Team page showing invite status
    `,
    category: 'Getting Started',
    tags: ['invite', 'team', 'onboarding', 'access', 'collaboration'],
    author: 'Flowsense Support',
    publishedDate: '2024-02-05',
    lastUpdated: '2024-03-15',
    readTime: '2 min read',
    views: 1800,
    icon: UserPlus,
    faqs: [
      {
        question: "Why didn't my teammate receive the invite email?",
        answer:
          'Spam filters or a typo are most common—verify address and resend.',
      },
      {
        question: 'Can I invite a client outside my company?',
        answer: 'Yes—use read-only if appropriate.',
      },
      {
        question: 'Can I revoke an invite?',
        answer:
          'Usually yes (depends on your UI); otherwise support can remove access.',
      },
      {
        question: 'What happens when a teammate accepts the invite?',
        answer:
          'They become a member of your workspace with the assigned role.',
      },
      {
        question: 'How many teammates can I add?',
        answer:
          "Depends on your plan; if you hit a limit you'll see a paid plan prompt.",
      },
    ],
    relatedArticles: ['17', '19', '16'],
  },
  {
    id: '19',
    slug: 'managing-team-access',
    title:
      'Managing Team Access: Changing Roles, Removing Members, Offboarding',
    excerpt:
      'Learn how to change team member roles, remove access, and safely offboard users.',
    content: `
## Quick Answer

You can change a member's role or remove them from the workspace to revoke access.

## Step-by-Step (Recommended Safe Offboarding)

1. Change role to **Read-only** (optional interim step)
2. Remove user from workspace
3. Rotate any shared credentials outside Flowsense (if applicable)

## Changing a Member's Role

1. Go to **Settings → Team**
2. Find the member
3. Click role dropdown
4. Select new role
5. Save changes

## Removing a Member

1. Go to **Settings → Team**
2. Find the member
3. Click **Remove** (trash icon)
4. Confirm removal
5. They lose access immediately

## Common Gotchas

**Removing a user doesn't delete historical audit data:**
- It only removes their access
- Data remains in workspace

**If the removed user is the only Owner:**
- You may need to transfer ownership first
- System will prevent removing last Owner

## Troubleshooting

**"I can't remove someone":**
- You might not have permissions (need Owner/Admin)
- Can't remove last Owner without transferring first

**"I removed them but they still see it":**
- They may have access via another workspace
- Or cached session; ask them to refresh/re-login

## Escalation

Send to support:
- Workspace/org name
- Member email
- Desired end state (remove entirely vs downgrade role)
    `,
    category: 'Getting Started',
    tags: ['team', 'access', 'removal', 'offboarding', 'roles'],
    author: 'Flowsense Support',
    publishedDate: '2024-02-05',
    lastUpdated: '2024-03-15',
    readTime: '2 min read',
    views: 0,
    icon: Trash2,
    faqs: [
      {
        question: 'Can I remove a teammate immediately?',
        answer: 'Yes—removing them should revoke access immediately.',
      },
      {
        question: "Can I change a user's role later?",
        answer: 'Yes, anytime from Settings → Team.',
      },
      {
        question: 'Does removing a teammate delete our data?',
        answer: 'No—data remains in the workspace.',
      },
      {
        question: 'What if the person is the Owner?',
        answer: 'Transfer ownership first, then remove.',
      },
      {
        question: 'Can a client have access only to their reports?',
        answer:
          'Use Read-only and share exports; support can advise best practice.',
      },
    ],
    relatedArticles: ['17', '18', '20'],
  },
  {
    id: '20',
    slug: 'deleting-organization',
    title:
      'Deleting an Organization: What Happens to Billing, Access, and Data',
    excerpt:
      'Understand what happens when you delete a workspace and how it affects billing and data.',
    content: `
## Quick Answer

Deleting a workspace removes access and deletes that workspace's configuration/data. It does not automatically guarantee a refund (refunds follow the 14-day last payment policy).

## When to Use This

- You created a workspace by mistake
- A client churned and you want to clean up
- You want to permanently remove a workspace

## Before You Delete (Agency Checklist)

- [ ] Export any reports you need for records
- [ ] Confirm billing status and whether you should cancel first
- [ ] Ensure you're deleting the correct workspace (not your main agency workspace)
- [ ] Notify team members who have access

## Step-by-Step

1. Go to **Settings → Danger Zone / Organization**
2. Request deletion confirmation code (if required)
3. Confirm deletion (type required phrase / enter code)
4. You'll be logged out or redirected if the org no longer exists

## What Happens to Billing

Deleting a workspace is not the same as canceling a subscription in all systems.

**Best practice:**
1. Cancel subscription (via billing portal) if you don't want future charges
2. Then delete the org if you want the workspace removed

## Refund Note (Policy Consistency)

- Refunds are possible within 14 days of the last payment only
- Deleting an org does not automatically trigger a refund
- You must request a refund separately

## Troubleshooting

**If deletion fails:**
- Check you have Owner permissions
- Retry with a fresh code
- Contact support if error persists

**If you deleted the wrong org:**
- Contact support immediately
- Recovery may not be possible
- Treat deletion as permanent

## Escalation

Send to support:
- Workspace/org name
- Confirmation of who requested deletion
- Timestamp and any error message
    `,
    category: 'Getting Started',
    tags: ['deletion', 'workspace', 'organization', 'data', 'billing'],
    author: 'Flowsense Support',
    publishedDate: '2024-02-10',
    lastUpdated: '2024-03-15',
    readTime: '3 min read',
    views: 0,
    icon: Trash2,
    faqs: [
      {
        question: 'Does deleting my workspace cancel billing?',
        answer: 'Not always—cancel in the billing portal to stop renewals.',
      },
      {
        question: 'Can I recover a deleted workspace?',
        answer: 'Usually no. Treat deletion as permanent.',
      },
      {
        question: 'Will I lose exports/reports I downloaded?',
        answer:
          'No—anything exported locally remains, but in-app history may be removed.',
      },
      {
        question: "Can I delete a workspace if I'm not the Owner?",
        answer: 'Typically no.',
      },
      {
        question: 'Can I get a refund if I delete the org?',
        answer:
          "Only if it's within 14 days of the last payment, and you request it.",
      },
    ],
    relatedArticles: ['13', '16', '19'],
  },
  {
    id: '21',
    slug: 'paid-plan-required-errors',
    title: '"Paid Plan Required" Errors: What They Mean and How to Resolve',
    excerpt:
      'Understand why you see "Paid plan required" messages and how to unlock features.',
    content: `
## Quick Answer

"Paid plan required" means you tried to use a feature that's locked to Pro (or above). It can also appear if your subscription is inactive/past due.

## Common Situations That Trigger It

- Adding or managing team members
- White-label/branding features
- Custom domains
- Advanced reporting/export features
- Deliverability alerts configuration

## Step-by-Step: Fix It

1. Go to **Billing** page
2. Confirm your current plan and status
3. **If you're Free:**
   - Upgrade via checkout
4. **If you believe you are Pro but still blocked:**
   - Refresh, log out/in
   - Confirm Stripe payment succeeded
   - Wait a couple minutes for sync
5. **If still blocked:**
   - Contact support with invoice ID

## Troubleshooting

**If you upgraded but the UI still shows Free:**
- This is usually a sync delay or webhook issue
- Provide invoice ID to support for fast resolution

**If payment failed:**
- Check billing status
- Update payment method
- See "Failed Payments" article

## Escalation

Send to support:
- Workspace/org name
- Screenshot of Billing page
- Invoice ID (best)
- The exact blocked action you tried to do
    `,
    category: 'Billing',
    tags: ['paid plan', 'upgrade', 'features', 'locked', 'troubleshooting'],
    author: 'Flowsense Support',
    publishedDate: '2024-02-15',
    lastUpdated: '2024-03-18',
    readTime: '2 min read',
    views: 0,
    icon: Lock,
    faqs: [
      {
        question: 'Why am I seeing "Paid plan required"?',
        answer:
          "You're trying to use a Pro feature on a Free plan, or your subscription isn't active.",
      },
      {
        question: 'Which features are Pro-only?',
        answer:
          'Typically team management, white-label branding, custom domains, advanced exports/alerts.',
      },
      {
        question: 'I paid—why am I still blocked?',
        answer:
          'Sync may be delayed; confirm invoice, then contact support with invoice ID.',
      },
      {
        question: 'Does each teammate need a paid seat?',
        answer:
          'Depends on how pricing is structured; most commonly the workspace plan governs access.',
      },
      {
        question: 'Can I trial Pro?',
        answer: 'Contact sales/support for trial options.',
      },
    ],
    relatedArticles: ['11', '15', '10'],
  },

  // KLAVIYO INTEGRATION
  {
    id: '22',
    slug: 'connect-klaviyo',
    title: 'Connect Klaviyo to Flowsense (OAuth vs API Key)',
    excerpt:
      'Step-by-step guide to connecting your Klaviyo account using OAuth or API key methods.',
    content: `
## Quick Answer

Connect Klaviyo so Flowsense can read your flow configuration and generate recommendations. OAuth is preferred; API key is a fallback.

## When to Use This

- You're setting up a new client or brand
- You reconnected after permissions changed
- You see "no flows found" or "unauthorized"

## Step-by-Step (OAuth - Recommended)

1. Go to **Settings → Integrations → Klaviyo**
2. Click **Connect Klaviyo**
3. Select the correct Klaviyo account
4. Approve requested permissions
5. Return to Flowsense and wait for initial sync

## Step-by-Step (API Key - Fallback)

1. In Klaviyo, generate an API key (follow client policy)
2. In Flowsense, choose **Connect via API key**
3. Paste key and save
4. Confirm the connection status and data load

## What Permissions We Need

- Read access to flows (structure and configuration)
- Read access to metrics (performance data)
- Read access to templates (content analysis)

**We do NOT need:**
- Write access (we don't modify flows)
- Access to customer lists
- Sending permissions

## Troubleshooting

**Wrong account selected:**
- Disconnect and reconnect
- Ensure you select the correct account during OAuth

**Permissions insufficient:**
- Reconnect with correct role in Klaviyo
- Ensure authorizing user has Admin/Manager access

**Client revoked access:**
- Reconnect; if still blocked, client must re-authorize

## Escalation

Send to support:
- Org/workspace name
- Klaviyo account name
- Connection method (OAuth/API key)
- Screenshots of errors
- Timestamp
    `,
    category: 'Integrations',
    tags: ['klaviyo', 'connection', 'oauth', 'api key', 'setup'],
    author: 'Flowsense Team',
    publishedDate: '2024-01-15',
    lastUpdated: '2024-03-20',
    readTime: '4 min read',
    views: 0,
    icon: Link2,
    faqs: [
      {
        question: 'Is Flowsense read-only?',
        answer:
          'Yes, Flowsense should not modify flows automatically. It needs access to read configuration and analyze.',
      },
      {
        question: 'Which connection method is best?',
        answer:
          'OAuth (easier to rotate/maintain). API key if needed for policy.',
      },
      {
        question: 'Why do I see "not authorized"?',
        answer:
          "Usually the Klaviyo user role/permissions aren't sufficient or authorization was revoked.",
      },
      {
        question: 'Can I connect multiple Klaviyo accounts?',
        answer:
          'Yes for agency workflows, depending on how clients are modeled in your workspace.',
      },
      {
        question: 'Will reconnecting delete anything?',
        answer: 'No—reconnecting should only refresh access.',
      },
    ],
    relatedArticles: ['23', '24', '1'],
  },
  {
    id: '23',
    slug: 'klaviyo-troubleshooting-no-flows',
    title: 'Klaviyo Troubleshooting: Connected But No Flows / Missing Data',
    excerpt:
      "Diagnose and fix issues when Klaviyo is connected but flows aren't showing up.",
    content: `
## Quick Answer

If Klaviyo is "connected" but Flowsense shows no flows or incomplete data, it's usually one of:
- Wrong account selected
- Missing permissions
- Sync not completed yet
- Client revoked access

## Step-by-Step Diagnosis

1. Confirm you're in the correct workspace
2. Confirm you're looking at the correct client/account
3. Confirm connection status (connected vs needs reauth)
4. Wait and refresh (first sync can take longer)
5. Reconnect if permissions changed

## Agency-Specific Checks

If you manage multiple clients:
- Ensure you didn't connect your own agency Klaviyo instead of the client's
- Ensure the client's access wasn't granted through a temporary user

## Troubleshooting

**"It worked yesterday":**
- Client may have revoked the integration
- OAuth token may have expired
- Reconnect to refresh

**"Some flows are missing":**
- Could be filtering context or a partial sync
- Reconnect and retry
- Verify flows exist in Klaviyo

## Escalation Checklist

Send to support:
- Workspace/org name
- Klaviyo account name
- Time you connected
- Screenshot of integration status
- Example flow names that are missing
    `,
    category: 'Integrations',
    tags: ['klaviyo', 'troubleshooting', 'sync', 'missing data', 'flows'],
    author: 'Flowsense Support',
    publishedDate: '2024-02-01',
    lastUpdated: '2024-03-15',
    readTime: '3 min read',
    views: 2400,
    icon: AlertCircle,
    faqs: [
      {
        question: "Why don't I see any flows after connecting?",
        answer:
          'Usually wrong account, missing permissions, or sync still in progress.',
      },
      {
        question: 'How long does the first sync take?',
        answer: 'Typically minutes, but can be longer for large accounts.',
      },
      {
        question: 'Will reconnecting delete anything?',
        answer: 'No—reconnecting refreshes authorization and data sync.',
      },
      {
        question: 'Can client permissions cause missing flows?',
        answer: 'Yes—if access is limited, data will be incomplete.',
      },
      {
        question: 'How do I confirm I connected the right Klaviyo?',
        answer:
          'Check the account name shown during authorization and your integration status screen.',
      },
    ],
    relatedArticles: ['22', '24', '15'],
  },
  {
    id: '24',
    slug: 'klaviyo-permissions-reconnection',
    title: 'Klaviyo Permissions & Reconnection: "Not Authorized" / "Forbidden"',
    excerpt:
      'Fix authorization errors and reconnect Klaviyo when permissions are revoked or insufficient.',
    content: `
## Quick Answer

These errors almost always mean your Klaviyo authorization is missing required permissions or has been revoked.

## Step-by-Step Fix

1. Go to **Settings → Integrations → Klaviyo**
2. Click **Reconnect**
3. Ensure the Klaviyo user authorizing has sufficient access
4. Approve requested permissions
5. Return to Flowsense and refresh

## Best Practice for Agencies

- Have the client create a dedicated "integration admin" user or approved method
- Document who authorized the integration
- Avoid using personal accounts that can be deactivated

## Troubleshooting

**If reconnect keeps failing:**
- Try in an incognito window
- Disable aggressive privacy/ad blockers temporarily
- Confirm the client hasn't restricted app installations

**If client revoked access:**
- Contact client to re-authorize
- Explain what permissions are needed and why

## Escalation

Send to support:
- Screenshot of the Klaviyo error
- Workspace name + client account name
- The email of the Klaviyo user who authorized
    `,
    category: 'Integrations',
    tags: [
      'klaviyo',
      'permissions',
      'authorization',
      'reconnect',
      'troubleshooting',
    ],
    author: 'Flowsense Support',
    publishedDate: '2024-02-05',
    lastUpdated: '2024-03-15',
    readTime: '2 min read',
    views: 1600,
    icon: AlertCircle,
    faqs: [
      {
        question: 'Why do I see "not authorized"?',
        answer: 'Permission scope is missing, or the connection was revoked.',
      },
      {
        question: 'Can a client revoke access at any time?',
        answer: 'Yes—then you must reconnect.',
      },
      {
        question: 'Does Flowsense need write access?',
        answer:
          'Typically no for audits; it depends on what features you enable.',
      },
      {
        question: 'Why does reconnect open a blank page or fail?',
        answer:
          'Often browser privacy settings or blocked third-party cookies.',
      },
      {
        question: 'How do I avoid this happening again?',
        answer: 'Use a stable integration user and keep documentation.',
      },
    ],
    relatedArticles: ['22', '23', '8'],
  },

  // FLOW AUDITS & RECOMMENDATIONS
  {
    id: '25',
    slug: 'running-flow-audit',
    title: "Running a Flow Audit: What's Analyzed and Why",
    excerpt:
      'Understand what happens during a flow audit and how to interpret the results.',
    content: `
## Quick Answer

An audit reviews flow setup patterns and generates actionable recommendations. It's designed to be repeatable (audit → fix → re-audit).

## Step-by-Step

1. Choose a client/account (agency) or your brand workspace
2. Select a flow (or flow set) to audit
3. Start the audit and wait for results
4. Review recommendations and sort by:
   - Severity / impact
   - Effort level
5. Implement changes in Klaviyo
6. Re-run audit to confirm improvements

## What We Analyze

### Flow Structure
- Number of emails
- Delay timing between emails
- Trigger logic and filters
- Exit conditions

### Content Quality
- Subject line effectiveness
- Personalization usage
- Spam trigger patterns
- Call-to-action clarity

### Performance Context
- Open rates vs benchmarks
- Click rates vs benchmarks
- Revenue per recipient
- Conversion patterns

### Configuration
- Smart sending settings
- UTM tracking
- Suppression logic
- A/B testing setup

## Troubleshooting

**Audit takes long:**
- Retry; first-time syncs can be slower
- Large accounts need more processing time

**Results look incomplete:**
- Confirm flows exist in Klaviyo
- Verify connection is active
- Check permissions

## Escalation

Send to support:
- Flow name
- The exact recommendation that seems wrong
- Screenshot of the flow configuration (redact sensitive text if needed)
    `,
    category: 'Best Practices',
    tags: ['audit', 'analysis', 'flows', 'recommendations', 'klaviyo'],
    author: 'Flowsense Product',
    publishedDate: '2024-01-20',
    lastUpdated: '2024-03-18',
    readTime: '4 min read',
    views: 0,
    icon: BarChart3,
    faqs: [
      {
        question: 'Does the audit change my Klaviyo flow?',
        answer: 'No, audits are read-only analysis.',
      },
      {
        question: 'How often should I run audits?',
        answer:
          'Agencies: monthly/quarterly, and after major lifecycle changes.',
      },
      {
        question: 'Can I audit only one flow?',
        answer: 'Yes—start with high-revenue lifecycle flows.',
      },
      {
        question: 'Why did the audit flag something I intentionally did?',
        answer:
          'Recommendations are guidance; you can intentionally deviate if you know your strategy.',
      },
      {
        question: 'How do I prove ROI to a client?',
        answer:
          'Export reports and track before/after metrics around the changed flows.',
      },
    ],
    relatedArticles: ['26', '1', '22'],
  },
  {
    id: '26',
    slug: 'understanding-recommendations',
    title:
      'Understanding Recommendations: Severity, Impact, and Prioritization',
    excerpt:
      'Learn how to prioritize and implement recommendations for maximum impact.',
    content: `
## Quick Answer

Treat recommendations like a backlog: prioritize by impact and effort, then re-audit to validate.

## Suggested Prioritization Framework

1. **Revenue blockers** (e.g., flow not sending, broken filters)
2. **Deliverability risks** (e.g., spam-trigger patterns, missing suppression logic)
3. **Segmentation/timing improvements** (better targeting, fewer drop-offs)
4. **Copy/creative upgrades** (conversion improvements)
5. **Polish** (formatting, naming, reporting)

## How to Present to Clients (Agency Playbook)

- "Top 3 issues we fixed this week"
- "Top 5 backlog items for next sprint"
- "Expected impact + why it matters"
- "Proof: re-audit + screenshots + export"

## Recommendation Severity Levels

### Critical (Red)
- Immediate action required
- Blocking revenue or causing deliverability issues
- Examples: Flow not sending, broken trigger

### High (Orange)
- Significant impact on performance
- Should be addressed soon
- Examples: Missing suppression, poor timing

### Medium (Yellow)
- Moderate impact
- Address when capacity allows
- Examples: Suboptimal subject lines, missing personalization

### Low (Blue)
- Nice-to-have improvements
- Polish and optimization
- Examples: Better formatting, additional tracking

## Troubleshooting

**If recommendation feels wrong:**
- Check assumptions (flow goal, trigger, exclusions)
- Consider your specific strategy and constraints

**If client has constraints:**
- Translate recommendation into a compatible alternative
- Document why you're deviating from best practice

## Escalation

Send to support:
- Recommendation title
- Why you believe it's incorrect
- Screenshot of relevant Klaviyo settings
    `,
    category: 'Best Practices',
    tags: [
      'recommendations',
      'prioritization',
      'impact',
      'agency',
      'implementation',
    ],
    author: 'Flowsense Strategy',
    publishedDate: '2024-01-25',
    lastUpdated: '2024-03-18',
    readTime: '5 min read',
    views: 3200,
    icon: Zap,
    faqs: [
      {
        question: 'Are recommendations guaranteed to increase revenue?',
        answer:
          "No tool can guarantee outcomes; they're best-practice guidance that typically improves performance.",
      },
      {
        question: 'Can I dismiss a recommendation?',
        answer:
          'Yes; you can decide not to implement it if it conflicts with strategy.',
      },
      {
        question: 'Which recommendations should I do first?',
        answer:
          'High impact + low effort, especially deliverability and targeting.',
      },
      {
        question: 'Can recommendations be client-specific?',
        answer:
          "Yes—always contextualize to the client's brand, offer, and list health.",
      },
      {
        question: 'How do I validate improvements?',
        answer: 'Re-audit + compare key metrics in Klaviyo before/after.',
      },
    ],
    relatedArticles: ['25', '27', '1'],
  },

  // REPORTS & WHITE-LABEL
  {
    id: '27',
    slug: 'exporting-reports',
    title: 'Exporting Reports (PDF/HTML) + Client Delivery Workflows',
    excerpt:
      'Learn how to export professional reports and deliver them to clients effectively.',
    content: `
## Quick Answer

Exports turn your audit into a client-ready deliverable. Use PDF for stakeholders and HTML for interactive review/share workflows.

## When to Use This

- Monthly client check-ins
- Audit delivery after an optimization sprint
- Internal QA and documentation

## Recommended Workflow (Agency Playbook)

1. Run audit
2. Fix the top items
3. Re-audit to confirm improvements
4. Export report:
   - **PDF** for email/Slack
   - **HTML** for sharing in a client portal
5. Store exports in your client folder with:
   - Date stamp
   - Version tag
   - "What changed" notes

## Step-by-Step: Export a Report

1. Navigate to the audit or dashboard
2. Click **Export** button
3. Choose format (PDF or HTML)
4. Select date range (if applicable)
5. Click **Download**
6. Report generates and downloads

## Export Formats

### PDF
- Best for: Email attachments, presentations
- Includes: Charts, health scores, recommendations
- White-label: Uses your branding (if configured)

### HTML
- Best for: Sharing via link, client portals
- Includes: Interactive elements, full data
- White-label: Uses your branding (if configured)

## Troubleshooting

**Missing styles in PDF:**
- Try another browser or disable aggressive privacy extensions
- Clear cache and retry

**Export is blank:**
- Refresh page and retry
- Ensure data has loaded completely

**Export is slow:**
- Large reports can take longer
- Try exporting one flow at a time

## Escalation

Send to support:
- Export type (PDF/HTML)
- Browser + version
- Screenshot of the broken export
- Any console/network error
    `,
    category: 'Best Practices',
    tags: ['reports', 'export', 'pdf', 'html', 'agency', 'delivery'],
    author: 'Flowsense Product',
    publishedDate: '2024-02-15',
    lastUpdated: '2024-03-18',
    readTime: '3 min read',
    views: 2800,
    icon: Download,
    faqs: [
      {
        question: 'Should I send clients PDF or HTML?',
        answer: 'PDF for simple delivery; HTML for richer review.',
      },
      {
        question: 'Why is my PDF missing styling?',
        answer:
          'Often a browser extension or printing engine issue; try another browser.',
      },
      {
        question: 'Can I export multiple flows at once?',
        answer: 'If supported—yes; otherwise export per flow/report section.',
      },
      {
        question: 'Does export include white-label branding?',
        answer:
          'If enabled on your plan and configured in branding settings—yes.',
      },
      {
        question: 'Where do exports live after download?',
        answer:
          'On your device (Downloads) unless you upload them to your client folder system.',
      },
    ],
    relatedArticles: ['28', '26', '17'],
  },

  // NEW TROUBLESHOOTING ARTICLES
  {
    id: '28',
    slug: 'troubleshooting-klaviyo-connection',
    title:
      "Troubleshooting: Flowsense Can't Connect to Klaviyo (API Key / Permissions / Rate Limits)",
    excerpt:
      'Diagnose and fix connection issues between Flowsense and Klaviyo including API key problems, permissions, and rate limits.',
    content: `
## Overview

If Flowsense can't connect to Klaviyo, you'll typically see an error during initial setup or a "disconnected" state later. The most common causes are:

- Incorrect or revoked Klaviyo API key
- Wrong Klaviyo account/workspace (common for agencies)
- Permission scope issues (especially if you rotated keys)
- Temporary rate limits or Klaviyo outage

## Before You Start (Quick Checklist)

- [ ] Confirm you're logged into the correct Flowsense organization (agency vs brand workspace)
- [ ] Confirm you're using the correct Klaviyo account (especially if you manage multiple brands)
- [ ] If you recently rotated keys, assume the old key is invalid everywhere

## Step-by-Step Fix

### 1) Confirm Your Klaviyo API Key is Valid

In Klaviyo, create or locate the API key you intend to use.

Reconnect in Flowsense using that key.

If it connects successfully, the old key was likely revoked/rotated.

### 2) Ensure You Connected the Correct Klaviyo Account (Agency Pitfall)

If you manage multiple brands:
- Confirm which Klaviyo account you're logged into when generating the key
- Label keys in Klaviyo by client name to avoid mixups

### 3) Check for Permission/Scope Issues

If Flowsense connects but can't fetch flows/campaign data, it can indicate incomplete access:
- Rotate to a new key
- Reconnect Flowsense
- Re-run the sync (or wait for the next scheduled sync)

### 4) Handle Rate Limits (Temporary Failures)

**Symptoms:**
- Connection works sometimes, then fails
- Sync jobs time out or partially complete

**What to do:**
- Wait 15–60 minutes and try again
- Avoid repeatedly reconnecting (that can create more requests)

### 5) Verify the Organization/Client Selection Inside Flowsense

For agencies:
- Make sure you're connecting the integration under the correct client account (if Flowsense supports per-client connections in your setup)

## Common Error Messages (What They Usually Mean)

**"Unauthorized" / "Invalid key"**
- Key is wrong, revoked, or copied with extra whitespace

**"Forbidden" / "Insufficient permissions"**
- Key doesn't have required access, or Klaviyo-side permission model changed

**"Rate limit exceeded"**
- Temporary throttling; retry later

## Troubleshooting

**Wrong account connected:**
- Disconnect and reconnect with the correct Klaviyo account

**Permissions insufficient:**
- Reconnect with correct role in Klaviyo
- Ensure authorizing user has Admin/Manager access

**Rate limits:**
- Wait 15-60 minutes before retrying
- Don't spam reconnect attempts
    `,
    category: 'Troubleshooting',
    tags: [
      'klaviyo',
      'connection',
      'api key',
      'permissions',
      'rate limits',
      'troubleshooting',
    ],
    author: 'Flowsense Support',
    publishedDate: '2024-03-20',
    lastUpdated: '2024-03-20',
    readTime: '5 min read',
    views: 0,
    icon: AlertCircle,
    faqs: [
      {
        question: "How do I know if I'm using the wrong Klaviyo account?",
        answer:
          "If Flowsense shows data for a different brand (or no flows when you know flows exist), you're likely using a key from the wrong Klaviyo account. Recreate the key while logged into the correct brand's Klaviyo.",
      },
      {
        question:
          'I rotated my Klaviyo key—do I have to do anything in Flowsense?',
        answer:
          "Yes. After a key rotation, Flowsense must be updated with the new key. Old keys won't work.",
      },
      {
        question: 'Why does it connect but show missing flows?',
        answer:
          "Usually one of: You connected the wrong Klaviyo account, flows are in a different workspace/account, or sync hasn't completed yet (try again later).",
      },
      {
        question: 'Can rate limits cause the connection to fail?',
        answer:
          'Yes. If you try reconnecting repeatedly, it may worsen rate limiting. Wait and retry once.',
      },
      {
        question: "What should I send support if it still won't connect?",
        answer:
          'Timestamp of the attempt, the organization/client name in Flowsense, the exact error text, and whether the key was newly created or rotated.',
      },
    ],
    relatedArticles: ['22', '23', '24'],
  },
  {
    id: '29',
    slug: 'troubleshooting-audits-reports-wrong',
    title:
      'Troubleshooting: Audits or Reports Look Wrong (Missing Flows, Old Data, or Zero Results)',
    excerpt:
      'Diagnose and fix issues when audits or reports show incomplete, missing, or incorrect data.',
    content: `
## Overview

If an audit/report looks incomplete, it's rarely "random." Most issues fall into a few buckets:

- You're viewing the wrong organization/client
- Your Klaviyo connection changed (key rotated, permissions changed)
- Sync is still in progress
- Date range / filters exclude the data you expect

## Step-by-Step Diagnosis

### 1) Confirm You're in the Right Place

- Are you in the correct Flowsense organization?
- If you're an agency: are you looking at the correct client account?

### 2) Check Filters and Date Ranges

**Common mistakes:**
- Report set to a date range where the flow didn't exist yet
- Filtering to a subset (e.g., only "live" flows) when you expect drafts too

### 3) Confirm the Klaviyo Integration is Healthy

- If you see "disconnected," reconnect first
- If you recently rotated keys, assume historical pulls may be delayed until resync

### 4) Wait for Sync Completion (and Avoid Repeated Reruns)

If you just connected Klaviyo:
- Initial sync can take time depending on account size
- Repeated reruns can increase API usage and slow things down

### 5) Check for Renamed Flows / Changed Structures

Flows may appear "missing" if they were:
- Renamed
- Archived
- Moved/duplicated

## Common Scenarios & Fixes

**Scenario: "Zero results" but I know we have flows**
- Fix: Check date range and filters, confirm correct client selection, then confirm integration connection

**Scenario: "Old data"**
- Fix: Wait for the next sync window; if still stale, reconnect and verify key validity

**Scenario: "Some flows missing"**
- Fix: Confirm you're pulling from the right Klaviyo account and that flows weren't renamed/archived

## Troubleshooting

**Filters hiding data:**
- Reset filters to "All"
- Check date range includes the period you expect

**Wrong workspace/client:**
- Switch to correct organization
- Verify client selection in agency setup

**Sync delays:**
- Wait for scheduled sync
- Don't repeatedly trigger manual syncs
    `,
    category: 'Troubleshooting',
    tags: ['audits', 'reports', 'missing data', 'sync', 'troubleshooting'],
    author: 'Flowsense Support',
    publishedDate: '2024-03-20',
    lastUpdated: '2024-03-20',
    readTime: '4 min read',
    views: 0,
    icon: AlertCircle,
    faqs: [
      {
        question: 'Why do I see fewer flows than in Klaviyo?',
        answer:
          'Usually filters, wrong account/client selection, or flows being archived/renamed.',
      },
      {
        question:
          'Why do metrics not match exactly between Flowsense and Klaviyo?',
        answer:
          'Different tools may use different time windows, attribute events differently, or cache/refresh on different schedules. If the delta is large, confirm connection health and filters first.',
      },
      {
        question: 'How long does initial sync take?',
        answer:
          'It depends on the size of the Klaviyo account. If you just connected, allow time before assuming a failure.',
      },
      {
        question:
          'I connected the right account but reports are still empty—what next?',
        answer:
          "Reconnect once (don't spam reconnect), then verify the key, then escalate with timestamps and screenshots.",
      },
      {
        question: 'Can an agency manage multiple brands without mixing data?',
        answer:
          "Yes—just ensure each client's connection is created under the correct client account/workspace and that keys are labeled correctly.",
      },
    ],
    relatedArticles: ['23', '25', '28'],
  },
  {
    id: '30',
    slug: 'account-access-security',
    title:
      'Account Access & Security (Login, Email Verification, MFA, Password Resets)',
    excerpt:
      'Comprehensive guide to account access issues including login problems, verification, MFA, and password resets.',
    content: `
## Overview

This article covers the most common access issues:

- Didn't receive verification email
- Can't log in
- MFA email code not arriving
- Password reset not working

## Email Verification (Signup)

### If You Didn't Receive Your Verification Email

Try in this order:
1. Check spam/promotions folders
2. Confirm you typed your email correctly at signup
3. Use the "Resend verification email" action on the login page (if available)
4. Wait a few minutes and retry once (deliverability delays happen)

### If the Verification Link is Expired

Verification links may expire. Request a new verification email and use the latest link.

## MFA (Email Code) Issues

### If MFA Codes Aren't Arriving

1. Wait 1–3 minutes (email delays)
2. Check spam/promotions
3. Use "Resend code"
4. Make sure your mailbox isn't blocking automated emails (corporate filters can)

### If You Entered the Code and It Fails

- Codes are time-sensitive. Use the most recent code
- If you requested multiple codes, older codes may be invalid

## Password Reset

### If Password Reset Email Doesn't Arrive

1. Confirm the email address is correct
2. Check spam/promotions
3. Wait a few minutes before retrying

### If Reset Link Doesn't Work

- Use the most recent reset email
- Copy/paste the full URL (some clients break links)

## Security Best Practices (Recommended)

- Use a strong, unique password
- Rotate access if a teammate leaves your team
- Prefer agency operational emails over personal inboxes for shared ownership

## Troubleshooting

**Can't log in after signup:**
- Most common: email isn't verified yet
- Verify your email or resend verification

**MFA codes not arriving:**
- Check spam/promotions
- Wait a few minutes
- Use resend function

**Reset link expired:**
- Request a new reset link
- Complete process within the time limit
    `,
    category: 'Getting Started',
    tags: ['login', 'security', 'verification', 'mfa', 'password', 'access'],
    author: 'Flowsense Security',
    publishedDate: '2024-03-20',
    lastUpdated: '2024-03-20',
    readTime: '4 min read',
    views: 0,
    icon: Shield,
    faqs: [
      {
        question: "Why can't I log in after signing up?",
        answer:
          "Most commonly: your email isn't verified yet. Verify your email or resend the verification email.",
      },
      {
        question:
          'I clicked "Resend verification email" — what should I expect?',
        answer:
          'For privacy and security, systems often respond "success" even if the email isn\'t registered. Check your inbox/spam and try again after a short delay.',
      },
      {
        question:
          "I'm not receiving MFA codes—does that mean Flowsense is down?",
        answer:
          'Not necessarily. Email deliverability delays or spam filtering are common. Try resend and check spam/promotions first.',
      },
      {
        question: 'I used the wrong email at signup—what do I do?',
        answer:
          'Sign up again with the correct email, or contact support to recover the account (include both emails if possible).',
      },
      {
        question: 'Can I change my login email?',
        answer:
          'If supported in your account settings, update it there. If not available, escalate to support for a secure identity update process.',
      },
    ],
    relatedArticles: ['5', '6', '7', '8'],
  },
  {
    id: '31',
    slug: 'agency-managing-multiple-clients',
    title:
      'Agency Workflow: Managing Multiple Client Accounts (Best Practices)',
    excerpt:
      'Best practices for agencies managing multiple client accounts without mixing data or losing organization.',
    content: `
## Overview

Flowsense is built to support agencies managing multiple brands. The keys to staying organized are:

- Clean client naming conventions
- Clear ownership and access roles
- Correct integration setup per client
- Repeatable reporting cadence

## Recommended Setup (Agency Baseline)

### 1) Use One Agency Organization as the "Control Tower"

- Your agency team lives here
- You manage client accounts under it (as applicable)

### 2) Standardize Client Naming

Use consistent naming like:
- **BrandName - Region**
- **BrandName - Storefront**
- **BrandName (Klaviyo: account-id suffix)**

### 3) Enforce Role Hygiene

- Limit "Owner/Admin" to a few trusted operators
- Give clients limited roles if they need visibility

### 4) Prevent Integration Mixups

For each client:
- Generate Klaviyo keys from the correct Klaviyo account
- Label keys and store them securely (not in random docs)
- Reconnect immediately after rotations

## Operating Cadence (What Works in Practice)

**Weekly:** Quick audit sweep + deliverability check

**Monthly:** Deeper performance report + change log

**Quarterly:** Flow architecture review + sunset old experiments

## Common Mistakes (And How to Avoid Them)

**Mistake:** Connecting the wrong Klaviyo account to the wrong client
- **Fix:** Always validate by spot-checking 1–2 known flows after connecting

**Mistake:** Too many admins
- **Fix:** Keep admin roles tight; create a process for access requests

## Best Practices

**Client Onboarding Checklist:**
1. Create client in Flowsense with standardized name
2. Generate and label Klaviyo API key
3. Connect integration and validate with known flows
4. Set up team access with appropriate roles
5. Configure white-label branding (if applicable)
6. Schedule first audit

**Documentation:**
- Maintain a client integration spreadsheet
- Document who authorized each Klaviyo connection
- Track key rotation dates
- Keep client contact information current

**Security:**
- Review team access quarterly
- Remove departed team members immediately
- Rotate shared credentials when staff changes
- Use least-privilege roles by default
    `,
    category: 'Best Practices',
    tags: ['agency', 'workflow', 'clients', 'organization', 'best practices'],
    author: 'Flowsense Agency Team',
    publishedDate: '2024-03-20',
    lastUpdated: '2024-03-20',
    readTime: '5 min read',
    views: 0,
    icon: Users,
    faqs: [
      {
        question: "How do we ensure we don't mix up client data?",
        answer:
          'Use strict naming, validate after connecting, and maintain a documented "client integration checklist."',
      },
      {
        question: 'Can clients be invited into the agency workspace?',
        answer: 'If your setup supports it, yes. Use least-privilege roles.',
      },
      {
        question:
          'What happens if a client rotates their Klaviyo key without telling us?',
        answer:
          'Sync or audits may fail or go stale. Reconnect using the new key and rerun/refresh.',
      },
      {
        question: 'How should we handle staff turnover?',
        answer:
          'Remove team access immediately, rotate any shared credentials, and review org roles.',
      },
      {
        question: 'How do we present reports to clients?',
        answer:
          'Use consistent monthly exports and a "What changed / What we recommend / What we need from you" structure.',
      },
    ],
    relatedArticles: ['16', '17', '27', '4'],
  },
  {
    id: '32',
    slug: 'billing-cancellations-refunds',
    title:
      'Billing Support: Cancellations, Downgrades, Refunds (Policy), and Common Billing Issues',
    excerpt:
      'Complete guide to canceling subscriptions, requesting refunds, and troubleshooting common billing problems.',
    content: `
## Overview

This article explains how to:

- Cancel or change your subscription
- Understand what happens after cancellation
- Request a refund (and what qualifies)
- Troubleshoot billing problems

## Cancellations & Downgrades

### Canceling

When you cancel, you may retain access until the end of your current billing period (depending on your subscription settings). After that, paid features may become unavailable.

### Downgrading

Downgrading reduces access to paid-only features. If you rely on team management, branding, or certain advanced features, confirm impact before downgrading.

## Refund Policy (Important)

Flowsense refunds are available **only within 14 days of your last payment**.

- Refund eligibility is based on the **most recent charge** (your "last payment"), not the original signup date
- Requests outside the 14-day window are not eligible

## Common Billing Issues & Fixes

### "Paid Plan Required"

This usually means you're trying to use a paid-only feature on a free plan.

**Fix:**
- Upgrade, or
- Remove/avoid paid-only actions (team changes, advanced branding, etc.)

### "Checkout Session Failed" or "Price Not Found"

Typically caused by:
- A mismatch between test/live Stripe configuration
- A discontinued price ID
- Temporary Stripe issues

**Fix:** Retry once and then escalate with timestamp + org name

### Card Was Charged but Plan Didn't Update

This is usually a webhook sync delay.

**Fix:** Wait a few minutes, refresh, then escalate if unchanged

## How to Cancel Your Subscription

1. Go to **Billing** page
2. Click **Manage Subscription** (opens Stripe portal)
3. Click **Cancel subscription**
4. Select reason (optional)
5. Confirm cancellation
6. You'll receive confirmation email

## How to Request a Refund

1. Go to **Billing** page and open Stripe portal
2. Find the most recent invoice/payment date
3. Contact support with:
   - Organization name
   - Billing email
   - Date of last payment
4. Refunds are only available within 14 days of the last payment

## Troubleshooting

**Canceled but still charged:**
- Check cancellation date vs renewal date
- Cancellation takes effect at period end

**Subscription status looks wrong:**
- Wait a few minutes and refresh
- Contact support with org name and timestamp
    `,
    category: 'Billing',
    tags: ['billing', 'cancellation', 'refund', 'downgrade', 'troubleshooting'],
    author: 'Flowsense Billing',
    publishedDate: '2024-03-20',
    lastUpdated: '2024-03-20',
    readTime: '4 min read',
    views: 0,
    icon: CreditCard,
    faqs: [
      {
        question: 'What is your refund policy?',
        answer:
          "Refunds are available only within 14 days of the last payment. Requests outside that window aren't eligible.",
      },
      {
        question: 'I canceled—why can I still access features?',
        answer:
          'Many subscriptions remain active until the billing period ends. Your access changes when the period ends (or per your subscription settings).',
      },
      {
        question: "I was charged but my plan didn't update.",
        answer:
          'It may be a webhook sync delay. Wait a few minutes and refresh; if it persists, escalate with org name and payment timestamp.',
      },
      {
        question: 'Do you refund annual plans?',
        answer:
          "If the annual plan payment was within the last 14 days, it can be eligible. Outside that window, it's not eligible.",
      },
      {
        question: 'Why am I seeing "Paid plan required"?',
        answer:
          "You're attempting a paid feature while on a free plan. Upgrade to unlock, or avoid that feature.",
      },
    ],
    relatedArticles: ['9', '12', '13', '14'],
  },

  // DELIVERABILITY ALERTS
  {
    id: '33',
    slug: 'deliverability-alerts-how-they-work',
    title:
      'Deliverability Alerts: How They Work, What "Good" Looks Like, and What To Do Next',
    excerpt:
      'Understand deliverability alerts, what triggers them, and how to respond quickly to protect email performance.',
    content: `
## Overview

Flowsense deliverability alerts are designed to warn you when email performance behavior looks like it may be trending toward inboxing issues (or when a specific segment of your sending program changes sharply). Alerts are meant to be:

- **Actionable** (tell you what to check)
- **Early** (catch trend shifts)
- **Operational** (help agencies manage many brands)

## What Deliverability Alerts Typically Monitor

Exact alert conditions may evolve, but most systems like this track:

- Hard bounce rate spikes
- Spam complaint rate spikes
- Open/click rate drops (contextual / supporting signal)
- Sudden send volume changes
- Client-by-client anomalies (agency mode)

## What "Healthy" Looks Like (Rules of Thumb)

Use these as directional guidance (not absolute truth):

**Spam complaints:** Should be consistently low; sudden spikes matter more than a single day

**Hard bounces:** Consistent increases are a red flag (list hygiene or acquisition quality)

**Volume:** Big step-changes can trigger filtering, especially when paired with engagement drops

## What To Do When You Get an Alert (Fast Response)

### Step 1: Identify Scope

- Is it one client account or the whole organization?
- Is it one campaign/flow or broadly across sends?

### Step 2: Check "What Changed"

Common causes:
- New list source / acquisition channel
- New sending domain / authentication changes
- Creative/template change that triggered spam filtering
- Sudden increase in send frequency
- A broken segmentation rule (sending to unengaged users)

### Step 3: Take a Safe Mitigation Action

If the alert looks real:
- Reduce volume temporarily
- Exclude unengaged segments
- Pause the offending campaign/flow variant
- Investigate recent changes to DNS/authentication (SPF/DKIM/DMARC)

### Step 4: Decide if It Needs Escalation

Escalate when:
- Complaints/bounces remain elevated for multiple checks
- Multiple clients are impacted at once
- You suspect authentication/domain issues

## Agency Response Playbook

**For single-client alerts:**
1. Confirm scope (which flows/campaigns)
2. Review recent changes with client
3. Implement mitigation
4. Document in client record

**For multi-client alerts:**
1. Check for shared infrastructure changes
2. Review sending domain/DNS changes
3. Check for platform-wide issues
4. Escalate to support if systemic
    `,
    category: 'Best Practices',
    tags: ['deliverability', 'alerts', 'email', 'monitoring', 'agency'],
    author: 'Flowsense Deliverability',
    publishedDate: '2024-03-21',
    lastUpdated: '2024-03-21',
    readTime: '6 min read',
    views: 0,
    icon: Bell,
    faqs: [
      {
        question:
          'Are deliverability alerts "proof" we have deliverability problems?',
        answer:
          'No—alerts indicate risk signals. Confirm by checking recent campaign metrics and what changed.',
      },
      {
        question: 'Why did we get an alert after changing a template?',
        answer:
          "Template changes can affect spam filtering and engagement. If engagement drops + complaints rise, it's likely a real issue.",
      },
      {
        question: 'Can list growth trigger alerts?',
        answer:
          'Yes. Rapid list growth often comes with lower intent subscribers, which can raise bounces/complaints.',
      },
      {
        question: 'How should agencies respond differently than brands?',
        answer:
          "Agencies should confirm whether it's isolated to one client. If multiple clients are affected, look for shared infrastructure changes (sending domain provider, DNS changes, new shared template system).",
      },
      {
        question: "What's the first thing to do when spam complaints spike?",
        answer:
          'Stop the bleeding: Reduce volume, tighten targeting. Then audit acquisition sources and recent changes.',
      },
    ],
    relatedArticles: ['34', '25', '26'],
  },
  {
    id: '34',
    slug: 'troubleshooting-deliverability-alerts',
    title:
      'Troubleshooting Deliverability Alerts: False Positives, Missing Alerts, and "Why Did This Trigger?"',
    excerpt:
      'Diagnose false positives, understand why alerts trigger, and troubleshoot missing alerts.',
    content: `
## Overview

Deliverability alerting is detection-based, so you'll occasionally see:

- Alerts that feel "too sensitive"
- Alerts that don't match your intuition
- Situations where you expected an alert but didn't get one

This article helps you diagnose why.

## If You Think an Alert Is a False Positive

### 1) Check Sample Size

Small sends can create noisy metrics. A tiny number of complaints on a small send looks huge as a rate.

### 2) Check Segmentation Scope

Sometimes one segment (e.g., cold subscribers) can distort the overall rate.

### 3) Check Time Windows

An alert might be using a short lookback window to detect sudden changes. That's often intentional.

### 4) Check for Multi-Variant Campaigns or Flow Branches

One variant can drive negative signals even if the overall campaign looks okay.

## If You Expected an Alert But Didn't Get One

Common reasons:

- The event hasn't been ingested/synced yet
- Thresholds weren't crossed
- The issue is gradual (no sudden spike)
- You're viewing a different client/org than the one impacted

## Why Alerts Often Trigger

- A new list source introduced low-quality subscribers
- A one-time blast hit a cold segment
- Authentication changes (SPF/DKIM/DMARC) weren't fully correct
- Inbox providers reacted to volume change

## Reducing Alert Noise Operationally

Create a standard playbook:

1. **Confirm sample size** - Is this based on enough sends?
2. **Confirm scope** - Which flows/campaigns are affected?
3. **Confirm "what changed"** - Recent changes in last 7 days?
4. **Only escalate when sustained** - One-time spikes may self-correct

## Troubleshooting Checklist

- [ ] Check send volume (small sends = noisy metrics)
- [ ] Check segment composition (cold vs engaged)
- [ ] Check time window (short windows = more sensitive)
- [ ] Check for A/B test variants
- [ ] Verify data sync is complete
- [ ] Compare with Klaviyo metrics directly
    `,
    category: 'Best Practices',
    tags: ['deliverability', 'alerts', 'troubleshooting', 'false positives'],
    author: 'Flowsense Deliverability',
    publishedDate: '2024-03-21',
    lastUpdated: '2024-03-21',
    readTime: '4 min read',
    views: 0,
    icon: AlertCircle,
    faqs: [
      {
        question: 'Can alerts trigger from one bad campaign?',
        answer:
          'Yes. One campaign to a cold segment can cause complaints/bounces that trip alerting.',
      },
      {
        question: 'Why do we get alerts right after a domain change?',
        answer:
          'New sending infrastructure or misconfigured DNS can cause immediate performance shifts.',
      },
      {
        question: 'What if metrics in Klaviyo look fine but Flowsense alerted?',
        answer:
          'Check the time window and segment scope. Also ensure data sync is complete.',
      },
      {
        question: "Should we turn off alerts if they're noisy?",
        answer:
          'Usually no—better to adjust operational handling: treat alerts as a triage queue, confirm scope, then act only when confirmed.',
      },
      {
        question: 'How do we reduce alert noise operationally?',
        answer:
          'Create a standard playbook: Confirm sample size, confirm scope, confirm "what changed", only escalate when sustained.',
      },
    ],
    relatedArticles: ['33', '25', '28'],
  },

  // CUSTOM DOMAINS & DNS
  {
    id: '35',
    slug: 'custom-domains-dns-troubleshooting',
    title:
      'Custom Domains & DNS Troubleshooting: Verification, CNAME Conflicts, SSL Pending',
    excerpt:
      'Troubleshoot custom domain setup including DNS verification, conflicts, SSL issues, and common pitfalls.',
    content: `
## Overview

Custom domains are powerful for branding, but DNS is unforgiving. Most issues come down to:

- Wrong record type (A vs CNAME)
- Conflicting records already present
- Proxy settings (e.g., Cloudflare orange-cloud)
- DNS propagation delays
- SSL certificate provisioning delays

## Common Setup Pattern (High-Level)

You typically:

1. Add a CNAME record from your custom hostname (e.g., app.brand.com) to the platform target
2. Verify DNS
3. Wait for SSL to provision

## Troubleshooting Checklist

### 1) DNS Record Exists and Is Correct

- Record type matches the requirement (often CNAME)
- Hostname is correct (no typos, correct subdomain)
- Target is correct (exact value provided)

### 2) Remove Conflicts

Common conflicts:
- Existing A record for the same hostname
- Multiple CNAME records (not allowed)
- Old records pointing to prior vendors

### 3) If Using Cloudflare: Check Proxy Mode

If you're using Cloudflare DNS:
- Try setting the record to **DNS Only** (grey cloud) during verification/provisioning
- Some setups fail SSL issuance when proxied prematurely

### 4) Wait for Propagation

DNS can take minutes to hours depending on TTL and provider.

### 5) SSL "Pending" States

SSL provisioning can lag behind DNS verification. If DNS is correct, wait, then retry verification.

## Symptom → Likely Cause

**"Verification failed"**
- DNS record incorrect, not propagated, or conflict exists

**"Hostname already in use"**
- Domain is attached elsewhere or previously configured—needs support intervention

**"SSL pending"**
- Usually propagation/provisioning delay or proxy interference

**"Not found" / 404**
- Domain points somewhere else, or the routing isn't attached to the correct org/hostname

## Step-by-Step: Verify Your DNS

1. Use a DNS lookup tool (dig, nslookup, or online checker)
2. Query your hostname (e.g., app.brand.com)
3. Confirm it returns the correct CNAME target
4. If it doesn't, check your DNS provider settings
5. Wait 5-10 minutes and check again

## Cloudflare-Specific Tips

- Start with DNS Only (grey cloud) during setup
- Once verified and SSL provisioned, you can enable proxy if needed
- Orange cloud can interfere with initial verification
    `,
    category: 'Troubleshooting',
    tags: ['custom domains', 'dns', 'verification', 'ssl', 'cloudflare'],
    author: 'Flowsense Infrastructure',
    publishedDate: '2024-03-21',
    lastUpdated: '2024-03-21',
    readTime: '5 min read',
    views: 0,
    icon: Globe,
    faqs: [
      {
        question: 'How long does DNS verification take?',
        answer:
          "Often minutes, sometimes longer depending on TTL/provider. If it's been hours, it's likely a record mismatch or conflict.",
      },
      {
        question:
          'Can I use an apex/root domain (brand.com) instead of a subdomain?',
        answer:
          'Many platforms strongly prefer subdomains (e.g., app.brand.com) because apex domains require ALIAS/ANAME support. If you need apex support, escalate to confirm feasibility.',
      },
      {
        question: 'Why does Cloudflare proxying break verification?',
        answer:
          'Proxying changes how traffic and certificates are handled. Some verification/SSL flows require DNS-only during provisioning.',
      },
      {
        question: 'Why is my CNAME correct but verification still fails?',
        answer:
          'Usually: propagation not complete, another conflicting record exists, or you edited the wrong DNS zone/account.',
      },
      {
        question: 'What should I provide to support?',
        answer:
          "The hostname you're trying to verify, screenshot of DNS records (type, name, target), whether Cloudflare proxy is enabled, and when you made the DNS change.",
      },
    ],
    relatedArticles: ['36', '22', '28'],
  },

  // SENDING DOMAIN TROUBLESHOOTING
  {
    id: '36',
    slug: 'sending-domain-troubleshooting',
    title:
      'Sending Domain Troubleshooting: SPF/DKIM/DMARC, Verification, and Common Pitfalls',
    excerpt:
      'Troubleshoot email authentication including SPF, DKIM, DMARC setup, verification, and common mistakes.',
    content: `
## Overview

Sending domains control authentication for outbound email. If SPF/DKIM/DMARC aren't correct, you may see:

- Poor inboxing
- Higher spam complaints
- Authentication failures
- Verification stuck/pending

## The 3 Records (Plain-English)

**SPF:** "Which servers are allowed to send for this domain?"

**DKIM:** "This message was signed; verify it wasn't altered."

**DMARC:** "What should receivers do if SPF/DKIM fails, and where should reports go?"

## Setup/Verification Checklist

### 1) Ensure You're Editing the Correct Domain

Common mistake: editing brand.com when you meant email.brand.com.

### 2) Copy Records Exactly

- Record names and values must match precisely
- Avoid extra spaces and line breaks

### 3) Avoid Duplicate SPF Records

You should usually have **one** SPF TXT record. Multiple SPF records can break SPF evaluation.

### 4) DKIM Record Formatting

DKIM is often long. Make sure your DNS provider supports long TXT values and doesn't silently truncate.

### 5) DMARC Alignment Basics

Start simple if you're new:
- Use a monitoring policy first (p=none) if needed
- Then move to stricter policies when stable

## Common Issues

**SPF "permerror" (too many DNS lookups)**
- Reduce nested includes; consolidate providers

**Record not found**
- Wrong host name, wrong DNS zone, propagation delay

**DKIM failing**
- Record truncated or incorrect selector/host

**DMARC policy too strict**
- Start with p=none for monitoring, then tighten

## Example SPF Record

\`\`\`
v=spf1 include:_spf.klaviyo.com include:_spf.google.com ~all
\`\`\`

## Example DKIM Record

\`\`\`
k=rsa; p=MIGfMA0GCSqGSIb3DQEBAQUAA4GNADCBiQKBgQC...
\`\`\`

## Example DMARC Record

\`\`\`
v=DMARC1; p=none; rua=mailto:dmarc@brand.com
\`\`\`

## Troubleshooting Steps

1. **Verify correct DNS zone** - Are you editing the right domain?
2. **Check for duplicates** - Only one SPF record allowed
3. **Validate syntax** - Use SPF/DKIM/DMARC validators
4. **Wait for propagation** - Can take 5-60 minutes
5. **Test with email** - Send test email and check headers
    `,
    category: 'Troubleshooting',
    tags: ['email', 'spf', 'dkim', 'dmarc', 'authentication', 'dns'],
    author: 'Flowsense Deliverability',
    publishedDate: '2024-03-21',
    lastUpdated: '2024-03-21',
    readTime: '6 min read',
    views: 0,
    icon: Mail,
    faqs: [
      {
        question: 'Do I need DMARC to send?',
        answer:
          'You can send without DMARC, but deliverability is often better with DMARC properly configured.',
      },
      {
        question: 'Why does verification say SPF failed when we set it?',
        answer:
          'You may have: multiple SPF records, edited the wrong domain/zone, or propagation not finished.',
      },
      {
        question: "What's the fastest way to debug?",
        answer:
          'Confirm: correct DNS zone, record exists, no duplicates, wait for propagation. Then escalate with screenshots if needed.',
      },
      {
        question: 'Can agencies reuse one sending domain across clients?',
        answer:
          'Not recommended. Each brand should own its sending identity to prevent cross-client reputation coupling.',
      },
      {
        question: 'Why did deliverability get worse after "fixing" DNS?',
        answer:
          'A DNS "fix" might have broken alignment or removed a necessary include. Roll back carefully and validate record integrity.',
      },
    ],
    relatedArticles: ['35', '33', '28'],
  },

  // PRIVACY & SECURITY
  {
    id: '37',
    slug: 'privacy-security-data-handling',
    title:
      'Privacy, Security, and Data Handling in Flowsense (Operational Overview)',
    excerpt:
      'Understand how Flowsense handles data, security best practices, and operational guidelines for agencies.',
    content: `
## Overview

This article explains how to think about data inside Flowsense operationally:

- What data is stored vs referenced
- How access is controlled
- What to do if you need exports or deletion
- How to safely work with client data as an agency

## Core Principles (Operational)

**Least privilege access:** Only give teammates what they need

**Segmentation of clients:** Keep client accounts clearly separated

**Change tracking:** Document major changes (domains, keys, template systems)

**Secure handling of credentials:** API keys should be rotated and never shared in plaintext docs

## What Data You Should Treat as Sensitive

- API keys (Klaviyo, email sending providers, etc.)
- Any exported performance reports tied to customer behavior
- Domain/DNS configuration details
- Team member access and role assignments

## Recommended Agency Practices

1. **Use role-based access** - Limit admins
2. **Remove access immediately on offboarding**
3. **Maintain an internal "client integration checklist" per brand**
4. **Use secure secret storage for keys** (not shared docs)

## Security Best Practices

### For API Keys
- Rotate keys quarterly or when staff changes
- Never commit keys to version control
- Use environment variables or secret managers
- Revoke old keys immediately after rotation

### For Team Access
- Review access quarterly
- Use least-privilege roles by default
- Remove departed team members same-day
- Document who has access to what

### For Client Data
- Only export what's necessary
- Use secure channels for sharing
- Delete exports after use
- Keep client data segregated

## What to Do If You Suspect a Security Issue

**Immediately:**
1. Rotate affected API keys
2. Remove suspicious users
3. Change passwords
4. Document the incident

**Then:**
- Contact support with timestamps and impacted org/client
- Review audit logs if available
- Notify affected clients if necessary
    `,
    category: 'Best Practices',
    tags: ['security', 'privacy', 'data', 'best practices', 'agency'],
    author: 'Flowsense Security',
    publishedDate: '2024-03-21',
    lastUpdated: '2024-03-21',
    readTime: '5 min read',
    views: 0,
    icon: Shield,
    faqs: [
      {
        question: 'Can I export my data?',
        answer:
          'If the product supports exports, use the export tools. Otherwise, contact support for an export request with scope and timeframe.',
      },
      {
        question: 'How do I request deletion?',
        answer:
          'Use the account/organization deletion process or contact support.',
      },
      {
        question: 'Are API keys stored securely?',
        answer:
          'Treat all keys as sensitive. Rotate keys if exposure is suspected.',
      },
      {
        question: 'How should agencies handle client access?',
        answer:
          'Only invite client users when necessary and use least privilege roles.',
      },
      {
        question: 'What should I do if I suspect unauthorized access?',
        answer:
          'Immediately: rotate keys, remove suspicious users, change passwords. Then contact support with timestamps and impacted org/client.',
      },
    ],
    relatedArticles: ['38', '39', '8'],
  },
  {
    id: '38',
    slug: 'data-export-requests',
    title:
      'Data Export Requests: What You Can Export and How To Ask for the Right Thing',
    excerpt:
      'Learn how to request data exports efficiently with the right scope and format.',
    content: `
## Overview

Exports are easiest when your request is specific. This article helps you request exports in a way that's fast and unambiguous.

## Best Practice: Define the Export Scope

When requesting an export, specify:

- **Organization/client**
- **Date range**
- **Data type** (audits, reports, alerts history, integrations status)
- **Output format preference** (CSV vs JSON vs PDF)

## Common Export Use Cases (Agencies)

- Monthly client reporting archive
- Migration handoff package
- Compliance requests from enterprise clients
- Historical performance analysis
- Audit trail documentation

## What To Include in an Export Request (Template)

\`\`\`
Org/client name: [Your Organization]
Date range: [Start Date] to [End Date]
Data type: [Audits / Reports / Alerts / All]
Reason (optional): [Monthly archive / Client handoff / Compliance]
Destination (email / secure link): [your@email.com]
\`\`\`

## Export Request Examples

### Example 1: Monthly Archive
\`\`\`
Org/client name: Acme Agency - Nike Account
Date range: March 1, 2024 to March 31, 2024
Data type: All audits and reports
Reason: Monthly client archive
Destination: reports@acmeagency.com
\`\`\`

### Example 2: Compliance Request
\`\`\`
Org/client name: Brand Co
Date range: January 1, 2024 to December 31, 2024
Data type: All audit history
Reason: Annual compliance review
Destination: compliance@brandco.com
Format: CSV preferred
\`\`\`

## Troubleshooting

**Export taking too long:**
- Narrow the date range
- Request specific data types only
- Split into multiple smaller requests

**Need multiple clients:**
- List each client separately
- Specify date range for each
- Consider staggered requests for large batches
    `,
    category: 'Best Practices',
    tags: ['export', 'data', 'compliance', 'reporting', 'agency'],
    author: 'Flowsense Support',
    publishedDate: '2024-03-21',
    lastUpdated: '2024-03-21',
    readTime: '3 min read',
    views: 0,
    icon: Download,
    faqs: [
      {
        question: 'Why do you need date ranges?',
        answer:
          'Without a date range, exports can be large and slow. Specific ranges speed up turnaround.',
      },
      {
        question: 'Can I get exports for multiple clients?',
        answer:
          'Yes—just list each client and desired date range. Larger batch exports may take longer.',
      },
      {
        question: 'Can you send exports to a client directly?',
        answer:
          'Typically exports should go to authorized org admins. If you need client delivery, confirm authorization and recipients.',
      },
      {
        question: "What's the fastest export request?",
        answer: 'One org/client + one data type + a clear date range.',
      },
      {
        question: "What if I don't know what I need?",
        answer:
          'Ask for help with your goal (e.g., "monthly performance archive") and support can suggest the minimal export.',
      },
    ],
    relatedArticles: ['37', '39', '27'],
  },
  {
    id: '39',
    slug: 'organization-account-deletion',
    title:
      'Organization & Account Deletion: How the Process Works (and What to Expect)',
    excerpt:
      'Understand the organization deletion process, what happens to data, and how to prepare.',
    content: `
## Overview

Deletion is intentionally gated for safety. Deleting an organization can impact:

- Team access
- Client visibility (agency mode)
- Reporting history
- Branding/domain configuration

## Recommended Before Deleting

1. **Export the reports you want to keep**
2. **Remove/rotate keys and revoke integration access**
3. **Confirm the right organization/client is being deleted**
4. **Notify team members**
5. **Cancel billing subscription** (if separate from deletion)

## Typical Deletion Flow (High-Level)

1. Deletion request is initiated by an authorized admin/owner
2. You may be asked to confirm via a code or confirmation step
3. Deletion executes after confirmation
4. Access is removed and the org is no longer active

## What Happens During Deletion

**Immediately:**
- Team members lose access
- Integrations are disconnected
- Custom domains are released

**After deletion:**
- Organization data is removed from active systems
- Backups may persist briefly for operational continuity
- Billing history remains for accounting purposes

## Common Mistakes

- Deleting the wrong org (agencies with many clients)
- Expecting deletion to undo billing history instantly
- Not exporting reports prior to deletion
- Not notifying team members first

## Step-by-Step: Delete an Organization

1. Go to **Settings → Danger Zone / Organization**
2. Click **Delete Organization**
3. Read the warnings carefully
4. Enter confirmation code or phrase
5. Confirm deletion
6. You'll be logged out or redirected

## For Agencies: Deleting a Single Client

If your setup supports client-level removal:
1. Navigate to the specific client
2. Use client-specific deletion option
3. Confirm you're deleting the right client
4. Export client reports first

If not supported, contact support with:
- Exact client name to delete
- Confirmation you're an owner/admin
- Whether you need an export first
    `,
    category: 'Best Practices',
    tags: ['deletion', 'organization', 'account', 'data', 'security'],
    author: 'Flowsense Support',
    publishedDate: '2024-03-21',
    lastUpdated: '2024-03-21',
    readTime: '4 min read',
    views: 0,
    icon: Trash2,
    faqs: [
      {
        question:
          'Can I delete only one client account without deleting the whole agency org?',
        answer:
          'If your setup supports client-level removal, use that feature. Otherwise, request support assistance specifying the exact client.',
      },
      {
        question: 'Is deletion immediate?',
        answer:
          'It may require confirmation and can take time to complete depending on safeguards.',
      },
      {
        question: 'Can deletion be undone?',
        answer: 'Usually not. Treat deletion as permanent.',
      },
      {
        question: 'What happens to team members?',
        answer:
          "They lose access to that organization's data once deletion completes.",
      },
      {
        question: 'What should I do before deleting?',
        answer:
          'Export what you need, confirm scope, and ensure billing and ownership expectations are understood.',
      },
    ],
    relatedArticles: ['37', '38', '20'],
  },

  // AUDIT READING & ACTION PLAYBOOKS
  {
    id: '40',
    slug: 'how-to-read-flow-audit',
    title: 'How to Read a Flowsense Flow Audit (What Each Section Means)',
    excerpt:
      "Learn how to interpret flow audits, understand what's being flagged, and turn findings into action.",
    content: `
## Overview

Flow audits are most valuable when you interpret them consistently. This guide helps you:

- Understand what an audit is flagging
- Avoid misinterpreting a "warning" as a "bug"
- Turn audit output into a plan

## The Mental Model

An audit typically evaluates:

**Coverage:** Do you have the right lifecycle flows?

**Structure:** Are triggers, filters, and timing set sanely?

**Content quality signals:** Are messages relevant and consistent?

**Operational risks:** Conflicts, overlaps, or missing guardrails

## How to Triage Audit Findings (3 Buckets)

### Bucket 1: "Must Fix Now" (Risk)

Examples:
- Broken trigger logic
- Sending to suppressed/unengaged users unintentionally
- Conflicting flows causing double-sends
- Missing critical suppression rules

### Bucket 2: "Should Fix Soon" (Performance)

Examples:
- Poor segmentation hygiene
- Missing personalization
- Timing friction / too many messages too fast
- Suboptimal subject lines

### Bucket 3: "Nice to Have" (Optimization)

Examples:
- Copy improvements
- Minor structural enhancements
- Additional experimentation suggestions
- Advanced personalization opportunities

## How to Validate an Audit Finding

1. Open the specific flow in Klaviyo
2. Confirm the trigger/filter and pathing
3. Cross-check send volume and recent performance
4. Identify if the "issue" is intentional for this brand

## Audit Sections Explained

### Flow Health Score
Overall assessment of flow configuration and performance

### Trigger Analysis
Evaluates trigger logic, filters, and entry conditions

### Message Timing
Reviews delays between emails and send frequency

### Content Quality
Assesses personalization, relevance, and spam risk

### Segmentation
Checks targeting logic and exclusion rules

### Performance Metrics
Compares against benchmarks and historical data

## Best Practices for Agency Teams

**Standardize severity mapping:**
- Now / Soon / Later
- Critical / High / Medium / Low

**Monthly cadence:**
- Run audits on the same schedule
- Track improvements over time

**Client-facing summary format:**
- What we found
- What we fixed
- What we recommend next
    `,
    category: 'Best Practices',
    tags: ['audits', 'analysis', 'flows', 'interpretation', 'agency'],
    author: 'Flowsense Strategy',
    publishedDate: '2024-03-21',
    lastUpdated: '2024-03-21',
    readTime: '6 min read',
    views: 520,
    icon: BarChart3,
    faqs: [
      {
        question:
          "Why does the audit say a flow is missing when we intentionally don't run it?",
        answer:
          'Audits check best-practice coverage. You can choose to skip flows intentionally—document the decision so it doesn\'t resurface as "work."',
      },
      {
        question: 'Should we fix everything the audit flags?',
        answer:
          'No. Prioritize by business impact and risk. A good audit becomes a prioritized backlog, not a to-do dump.',
      },
      {
        question: 'How do we avoid double-sends?',
        answer:
          'Audit for overlaps: multiple flows triggered by similar events, missing guardrails (e.g., "has received message in last X days").',
      },
      {
        question: 'How do we use audits across many clients?',
        answer:
          'Standardize: severity mapping (Now/Soon/Later), monthly cadence, client-facing summary format.',
      },
      {
        question: 'The audit seems wrong—what now?',
        answer:
          'Confirm integration connection, sync freshness, and correct client selection. Then escalate with specific examples.',
      },
    ],
    relatedArticles: ['41', '42', '25'],
  },
  {
    id: '41',
    slug: 'audit-to-action-plan-7-day',
    title: 'Playbook: Turn an Audit into a 7-Day Action Plan (Agency SOP)',
    excerpt:
      'Practical SOP for agencies to turn audit findings into concrete actions with a 7-day execution plan.',
    content: `
## Overview

This is a practical SOP to turn an audit into outcomes without thrash. It's designed for agencies who need repeatable execution.

## Day 0: Intake (30–60 minutes)

- Confirm correct client + correct Klaviyo account
- Confirm audit freshness (recent sync)
- Create a short "Top 5 issues" list

## Day 1–2: Fix Critical Risk Items

Focus only on:
- Misfires
- Double-sends
- Missing suppression/guardrails
- Obvious segmentation mistakes

**Output:**
- A changelog (what was changed, when, why)

## Day 3–4: Fix High-Impact Performance Items

Examples:
- Refine segmentation (engaged vs unengaged)
- Improve timing / message spacing
- Ensure core flows have consistent logic

**Output:**
- Updated flow map (simple doc is fine)

## Day 5: QA + Monitoring Setup

- Confirm key flows fire correctly
- Monitor complaint/bounce signals
- Review deliverability alerts

## Day 6–7: Report Back to Client

Use a consistent format:

### What We Changed
- List specific changes made
- Include before/after screenshots if helpful

### Why It Matters
- Expected impact on metrics
- Risk mitigation achieved

### What to Expect
- Timeline for results
- Metrics to watch

### What We Recommend Next
- Prioritized backlog items
- Suggested next sprint

## Prioritization Framework

### Top 5 Now (Days 1-2)
- Revenue blockers
- Deliverability risks
- Double-send conflicts

### Next 10 Soon (Days 3-4)
- Segmentation improvements
- Timing optimization
- Content quality

### Backlog Later (Future sprints)
- Advanced personalization
- A/B test opportunities
- Polish and optimization

## Change Management Best Practices

**Document everything:**
- What changed
- When it changed
- Why it changed
- Who approved it

**Test incrementally:**
- Don't change everything at once
- Validate each change
- Keep rollback plan ready

**Communicate proactively:**
- Notify client before major changes
- Explain expected impact
- Set clear expectations
    `,
    category: 'Best Practices',
    tags: ['playbook', 'agency', 'audits', 'action plan', 'sop'],
    author: 'Flowsense Agency Team',
    publishedDate: '2024-03-21',
    lastUpdated: '2024-03-21',
    readTime: '7 min read',
    views: 450,
    icon: Zap,
    faqs: [
      {
        question: 'What if the audit produces 50+ findings?',
        answer:
          "That's normal. Compress into: Top 5 now, Next 10 soon, backlog later.",
      },
      {
        question: 'Who should approve changes?',
        answer:
          "For agencies: internal owner approves, client approves if you're changing brand voice, cadence, or major segmentation strategy.",
      },
      {
        question: 'How do we avoid breaking revenue flows?',
        answer:
          'Make changes incrementally, and always document/revert path for critical flows.',
      },
      {
        question: 'How do we measure success?',
        answer:
          'Pick 2–3 metrics tied to the work (conversion, revenue per recipient, complaint rate, bounce rate).',
      },
      {
        question: 'Should we A/B test fixes?',
        answer:
          'For copy and creative changes: yes. For risk fixes (double-sends, broken triggers): no—just fix.',
      },
    ],
    relatedArticles: ['40', '42', '26'],
  },
  {
    id: '42',
    slug: 'how-to-read-reports',
    title:
      'How to Read Flowsense Reports (Performance, Trends, and "What Changed?")',
    excerpt:
      'Learn how to read performance reports, identify trends, and determine what actions to take.',
    content: `
## Overview

Reports are only useful when you can answer:

- What changed?
- Why did it change?
- What should we do next?

## The 3-Layer Report Read

### Layer 1: Trend
Are key metrics improving or declining?

### Layer 2: Attribution (What Drove the Change)
- Which flows/campaigns changed?
- Which segments changed?
- What external factors might be involved?

### Layer 3: Action
What do we stop, start, or continue?

## Common Misreads to Avoid

**Overreacting to a single day**
- Look for sustained patterns
- Consider day-of-week effects

**Ignoring sample size**
- Small sends create noisy metrics
- Focus on statistically significant changes

**Confusing correlation with causation**
- Especially after multiple changes
- Isolate variables when possible

## How to Identify What Caused a Change

### Check Recent Changes

**Send volume changes:**
- Did we send more/less?
- To different segments?

**Segmentation changes:**
- New targeting rules?
- Different audience mix?

**Deliverability alert signals:**
- Bounce rate changes?
- Complaint rate changes?

**Creative/template changes:**
- New design?
- Different messaging?

## The Executive Summary Format

Use this structure for client reports:

### 3 Wins
- What improved and by how much
- Why it improved
- What we'll continue doing

### 3 Issues
- What declined and by how much
- Why it declined
- What we're doing about it

### 3 Next Actions
- Specific changes planned
- Expected impact
- Timeline

## Separating Flows vs Campaigns

**Flows (Lifecycle/Automation):**
- Consistent, predictable patterns
- Changes indicate structural issues or opportunities

**Campaigns (Bursts):**
- Variable, promo-driven
- Changes indicate creative/offer performance

**Why separate them:**
- Mixing hides real causes
- Different optimization strategies
- Different success metrics

## Deliverability vs Offer Fatigue

### Deliverability Issues Show:
- Complaint/bounce changes
- Broad engagement drop across segments
- Authentication/domain problems

### Offer Fatigue Shows:
- Message-specific drop
- Segment-specific drop
- Gradual decline over time
    `,
    category: 'Best Practices',
    tags: ['reports', 'analysis', 'performance', 'trends', 'agency'],
    author: 'Flowsense Analytics',
    publishedDate: '2024-03-21',
    lastUpdated: '2024-03-21',
    readTime: '6 min read',
    views: 0,
    icon: BarChart3,
    faqs: [
      {
        question: 'Why do week-over-week numbers fluctuate so much?',
        answer:
          'Email can be seasonal and promo-driven. Look for sustained changes and correlate with known changes.',
      },
      {
        question: 'How do I identify what caused a decline?',
        answer:
          'Check: send volume changes, segmentation changes, deliverability alert signals, creative/template changes.',
      },
      {
        question: 'What\'s the best "executive summary" for clients?',
        answer: '3 wins, 3 issues, 3 next actions.',
      },
      {
        question: 'Should we report on flows and campaigns separately?',
        answer:
          'Yes—flows are lifecycle/automation; campaigns are bursts. Mixing them hides real causes.',
      },
      {
        question:
          "How do I know if it's a deliverability problem vs offer fatigue?",
        answer:
          'Deliverability issues often show: complaint/bounce changes, broad engagement drop. Offer fatigue is more message-specific/segment-specific.',
      },
    ],
    relatedArticles: ['40', '41', '27'],
  },
]
